import { state } from '../state.js';
import {
  StagingPackageSummary,
  StagingPackageItem,
  StagingCurateForm,
  StagingDeployForm,
  StagingImportState,
  StagingImportResult,
  StagingScenario,
  ScenarioPresetInfo,
  CompanySetupPresetConfig,
  ProductBatchPresetConfig,
  BackupSnapshotPresetConfig,
  TargetReadinessResult,
  TargetReadinessCheck,
  StagingDeployResult,
  StagingDeployLog,
} from '../types.js';
import { escapeHtml, formatRelativeTime, formatDateTime, formatFileSize } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';

export const SCENARIO_PRESETS: Record<StagingScenario, ScenarioPresetInfo> = {
  company_setup: {
    id: 'company_setup',
    name: 'Company Setup',
    shortLabel: 'Company Setup',
    icon: '🏢',
    badgeClass: 'badge-accent',
    title: 'Company Setup & Structure Entities',
    description: 'Foundation structure: locales, currencies, channels, attributes, families, categories, and reference entities.',
    recommendedName: 'Company Setup',
    recommendedDescription: 'Structural entity foundation: locales, channels, attributes, families, categories, and reference entities',
    features: [
      'Locales & Currencies',
      'Channels',
      'Attribute Groups & Attributes',
      'Families & Variants',
      'Category Trees',
      'Reference Entities',
    ],
  },
  product_batch: {
    id: 'product_batch',
    name: 'Product Batch',
    shortLabel: 'Product Batch',
    icon: '👟',
    badgeClass: 'badge-info',
    title: 'Product Batch & Catalog Selection',
    description: 'Curate product catalogs with family and category filters, auto-resolving attributes and media.',
    recommendedName: 'Product Batch Release',
    recommendedDescription: 'Curated product selection with family/category filters and auto-resolved schema dependencies',
    features: [
      'Family & Category filters',
      'Automatic dependency resolution',
      'Media file bundling',
      'Configurable batch limit',
    ],
  },
  backup_snapshot: {
    id: 'backup_snapshot',
    name: 'Backup Snapshot',
    shortLabel: 'Backup Snapshot',
    icon: '💾',
    badgeClass: 'badge-warning',
    title: 'Backup Snapshot & State Capture',
    description: 'Full environment state capture for point-in-time backup, disaster recovery, or rollback.',
    recommendedName: 'Full Backup Snapshot',
    recommendedDescription: 'Complete environment state capture for backup, rollback, and replication',
    features: [
      'Full state capture',
      'Referenced media assets',
      'Freeze & export ready',
      'One-click rollback',
    ],
  },
  custom: {
    id: 'custom',
    name: 'Custom Target',
    shortLabel: 'Custom',
    icon: '🛠️',
    badgeClass: 'badge-neutral',
    title: 'Blank Virtual Target',
    description: 'Create a blank virtual target to manually curate individual entities from Explorer or API.',
    recommendedName: 'Custom Virtual Target',
    recommendedDescription: 'Ad-hoc virtual target package for flexible entity curation',
    features: [
      'Blank canvas',
      'Curate via Entity Explorer',
      'Manual seed items',
      'Flexible dependencies',
    ],
  },
};

export const STAGING_ENTITY_TYPES = [
  { key: 'products', label: 'Products', icon: '👟' },
  { key: 'product_models', label: 'Product Models', icon: '📐' },
  { key: 'categories', label: 'Categories', icon: '🌲' },
  { key: 'families', label: 'Families', icon: '👔' },
  { key: 'family_variants', label: 'Family Variants', icon: '🧬' },
  { key: 'attributes', label: 'Attributes', icon: '🏷️' },
  { key: 'attribute_options', label: 'Attribute Options', icon: '🔘' },
  { key: 'attribute_groups', label: 'Attribute Groups', icon: '📑' },
  { key: 'association_types', label: 'Association Types', icon: '🔗' },
  { key: 'asset_families', label: 'Asset Families', icon: '🖼️' },
  { key: 'assets', label: 'Assets', icon: '📁' },
  { key: 'media_files', label: 'Media Files', icon: '📎' },
  { key: 'locales', label: 'Locales', icon: '🌐' },
  { key: 'currencies', label: 'Currencies', icon: '💱' },
  { key: 'channels', label: 'Channels', icon: '📺' },
  { key: 'reference_entities', label: 'Reference Entities', icon: '📚' },
  { key: 'reference_entity_records', label: 'Reference Records', icon: '📝' },
  { key: 'measurement_families', label: 'Measurement Families', icon: '📏' },
  { key: 'users', label: 'Users', icon: '👤' },
];

function safeJsonParse<T = Record<string, unknown>>(value: unknown): T | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value as T;
  try {
    return JSON.parse(value as string) as T;
  } catch {
    return null;
  }
}

function getEntityTypeLabel(key: string): string {
  return STAGING_ENTITY_TYPES.find((t) => t.key === key)?.label || key;
}

function getItemPayloadText(item: StagingPackageItem): string {
  const payload = safeJsonParse<Record<string, unknown>>(item.payload);
  if (!payload) return '';
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return '';
  }
}

/**
 * Initialize the Staging & Release Package Builder view.
 */
export function initStaging(): void {
  fetchStagingPackages();
  renderStagingPackages();
  renderStagingCategoryTabs();
  renderStagingItems();
  updateStagingBulkActionsUI();
  renderStagingGraph();
  renderStagingTargetSelect();
  renderStagingImportTargetSelect();
  updateStagingImportUI();
  initScenarioWizard();
}

/**
 * Initialize the Scenario Presets Wizard.
 */
export function initScenarioWizard(): void {
  renderScenarioPresetCards();
  renderScenarioConfigPanel();
  updateScenarioWizardSubmitButton();
  fetchScenarioOptions();
}

/**
 * Select a scenario preset in the Scenario Wizard.
 */
export function selectScenarioPreset(scenario: StagingScenario): void {
  const prevScenario = state.staging.scenarioWizard.selectedScenario;
  const prevPreset = SCENARIO_PRESETS[prevScenario] || SCENARIO_PRESETS.company_setup;
  const newPreset = SCENARIO_PRESETS[scenario] || SCENARIO_PRESETS.company_setup;

  state.staging.scenarioWizard.selectedScenario = scenario;

  const scenarioInput = document.getElementById('staging-package-scenario') as HTMLInputElement | null;
  if (scenarioInput) {
    scenarioInput.value = scenario;
  }

  const nameInput = document.getElementById('staging-package-name') as HTMLInputElement | null;
  const descInput = document.getElementById('staging-package-description') as HTMLInputElement | null;

  if (nameInput) {
    if (!nameInput.value.trim() || nameInput.value.trim() === prevPreset.recommendedName) {
      nameInput.value = newPreset.recommendedName;
      state.staging.scenarioWizard.packageName = newPreset.recommendedName;
    }
  }

  if (descInput) {
    if (!descInput.value.trim() || descInput.value.trim() === prevPreset.recommendedDescription) {
      descInput.value = newPreset.recommendedDescription;
      state.staging.scenarioWizard.packageDescription = newPreset.recommendedDescription;
    }
  }

  renderScenarioPresetCards();
  renderScenarioConfigPanel();
  updateScenarioWizardSubmitButton();
}

/**
 * Render the active state of scenario preset cards.
 */
export function renderScenarioPresetCards(): void {
  const current = state.staging.scenarioWizard.selectedScenario;
  const cards = document.querySelectorAll<HTMLElement>('#staging-scenario-cards .scenario-preset-card');
  cards.forEach((card) => {
    const cardScenario = card.getAttribute('data-scenario') as StagingScenario;
    const isActive = cardScenario === current;
    card.classList.toggle('active', isActive);
    card.setAttribute('aria-checked', isActive ? 'true' : 'false');
  });
}

/**
 * Render the dynamic config panels for the selected scenario.
 */
export function renderScenarioConfigPanel(): void {
  const current = state.staging.scenarioWizard.selectedScenario;
  const companyPanel = document.getElementById('staging-scenario-config-company');
  const productPanel = document.getElementById('staging-scenario-config-product');
  const backupPanel = document.getElementById('staging-scenario-config-backup');
  const customPanel = document.getElementById('staging-scenario-config-custom');

  if (companyPanel) companyPanel.style.display = current === 'company_setup' ? 'block' : 'none';
  if (productPanel) productPanel.style.display = current === 'product_batch' ? 'block' : 'none';
  if (backupPanel) backupPanel.style.display = current === 'backup_snapshot' ? 'block' : 'none';
  if (customPanel) customPanel.style.display = current === 'custom' ? 'block' : 'none';
}

/**
 * Reset the active scenario preset back to its defaults.
 */
export function resetScenarioPreset(): void {
  const current = state.staging.scenarioWizard.selectedScenario;
  const preset = SCENARIO_PRESETS[current] || SCENARIO_PRESETS.company_setup;

  const nameInput = document.getElementById('staging-package-name') as HTMLInputElement | null;
  const descInput = document.getElementById('staging-package-description') as HTMLInputElement | null;

  if (nameInput) nameInput.value = preset.recommendedName;
  if (descInput) descInput.value = preset.recommendedDescription;

  if (current === 'company_setup') {
    const localesCb = document.getElementById('staging-wizard-company-locales') as HTMLInputElement | null;
    const channelsCb = document.getElementById('staging-wizard-company-channels') as HTMLInputElement | null;
    const attrsCb = document.getElementById('staging-wizard-company-attributes') as HTMLInputElement | null;
    const familiesCb = document.getElementById('staging-wizard-company-families') as HTMLInputElement | null;
    const categoriesCb = document.getElementById('staging-wizard-company-categories') as HTMLInputElement | null;
    const refEntitiesCb = document.getElementById('staging-wizard-company-ref-entities') as HTMLInputElement | null;
    const autoFetchCb = document.getElementById('staging-wizard-company-auto-fetch') as HTMLInputElement | null;
    if (localesCb) localesCb.checked = true;
    if (channelsCb) channelsCb.checked = true;
    if (attrsCb) attrsCb.checked = true;
    if (familiesCb) familiesCb.checked = true;
    if (categoriesCb) categoriesCb.checked = true;
    if (refEntitiesCb) refEntitiesCb.checked = true;
    if (autoFetchCb) autoFetchCb.checked = false;
  } else if (current === 'product_batch') {
    const familySel = document.getElementById('staging-wizard-product-family') as HTMLSelectElement | null;
    const categorySel = document.getElementById('staging-wizard-product-category') as HTMLSelectElement | null;
    const limitInput = document.getElementById('staging-wizard-product-limit') as HTMLInputElement | null;
    const resolveDepsCb = document.getElementById('staging-wizard-product-resolve-deps') as HTMLInputElement | null;
    const includeMediaCb = document.getElementById('staging-wizard-product-include-media') as HTMLInputElement | null;
    const autoFetchCb = document.getElementById('staging-wizard-product-auto-fetch') as HTMLInputElement | null;
    if (familySel) familySel.value = '';
    if (categorySel) categorySel.value = '';
    if (limitInput) limitInput.value = '50';
    if (resolveDepsCb) resolveDepsCb.checked = true;
    if (includeMediaCb) includeMediaCb.checked = true;
    if (autoFetchCb) autoFetchCb.checked = false;
  } else if (current === 'backup_snapshot') {
    const scopeSel = document.getElementById('staging-wizard-backup-scope') as HTMLSelectElement | null;
    const mediaCb = document.getElementById('staging-wizard-backup-media') as HTMLInputElement | null;
    const freezeCb = document.getElementById('staging-wizard-backup-freeze') as HTMLInputElement | null;
    const autoFetchCb = document.getElementById('staging-wizard-backup-auto-fetch') as HTMLInputElement | null;
    if (scopeSel) scopeSel.value = 'full';
    if (mediaCb) mediaCb.checked = true;
    if (freezeCb) freezeCb.checked = false;
    if (autoFetchCb) autoFetchCb.checked = false;
  }

  updateScenarioWizardSubmitButton();
  showToast(`Preset "${preset.name}" reset to defaults`, 'info');
}

/**
 * Update the Scenario Wizard submit button label.
 */
export function updateScenarioWizardSubmitButton(): void {
  const submitBtn = document.getElementById('btn-create-staging-package');
  if (!submitBtn) return;

  const current = state.staging.scenarioWizard.selectedScenario;
  let isAutoFetch = false;

  if (current === 'company_setup') {
    const cb = document.getElementById('staging-wizard-company-auto-fetch') as HTMLInputElement | null;
    isAutoFetch = Boolean(cb && cb.checked);
  } else if (current === 'product_batch') {
    const cb = document.getElementById('staging-wizard-product-auto-fetch') as HTMLInputElement | null;
    isAutoFetch = Boolean(cb && cb.checked);
  } else if (current === 'backup_snapshot') {
    const cb = document.getElementById('staging-wizard-backup-auto-fetch') as HTMLInputElement | null;
    isAutoFetch = Boolean(cb && cb.checked);
  }

  submitBtn.innerHTML = isAutoFetch
    ? '<span>⚡ Create &amp; Populate Target</span>'
    : '<span>🚀 Create Virtual Target</span>';
}

/**
 * Fetch available options (families, categories) for scenario presets.
 */
export async function fetchScenarioOptions(): Promise<void> {
  const familySelect = document.getElementById('staging-wizard-product-family') as HTMLSelectElement | null;
  const categorySelect = document.getElementById('staging-wizard-product-category') as HTMLSelectElement | null;

  try {
    const [familiesRes, categoriesRes] = await Promise.all([
      fetch('/api/v1/discovery/explorer/families').catch(() => null),
      fetch('/api/v1/discovery/explorer/categories').catch(() => null),
    ]);

    if (familiesRes && familiesRes.ok) {
      const data = await familiesRes.json();
      const items = (data.items || []) as Array<{ code: string; label?: string }>;
      state.staging.scenarioWizard.availableFamilies = items;
      if (familySelect && items.length > 0) {
        familySelect.innerHTML = '<option value="">All Families (Any)</option>' +
          items.map((f) => `<option value="${escapeHtml(f.code)}">${escapeHtml(f.label || f.code)}</option>`).join('');
      }
    }

    if (categoriesRes && categoriesRes.ok) {
      const data = await categoriesRes.json();
      const items = (data.items || []) as Array<{ code: string; label?: string }>;
      state.staging.scenarioWizard.availableCategories = items;
      if (categorySelect && items.length > 0) {
        categorySelect.innerHTML = '<option value="">All Categories (Any)</option>' +
          items.map((c) => `<option value="${escapeHtml(c.code)}">${escapeHtml(c.label || c.code)}</option>`).join('');
      }
    }
  } catch {
    // Graceful fallback
  }
}

/**
 * Set the scenario filter for the staging packages list.
 */
export function setStagingScenarioFilter(scenarioFilter: string): void {
  state.staging.scenarioFilter = scenarioFilter || 'all';
  renderStagingPackages();
}

/**
 * Fetch all staging package definitions.
 */
export async function fetchStagingPackages(): Promise<void> {
  state.staging.loading = true;
  try {
    const res = await fetch('/api/v1/staging/packages');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.staging.packages = (data.packages || []) as StagingPackageSummary[];

    if (state.staging.selectedPackageId) {
      const pkg = state.staging.packages.find((p) => p.id === state.staging.selectedPackageId) || null;
      state.staging.selectedPackage = pkg;
      if (pkg) {
        const manifest = pkg.manifest
          ? (typeof pkg.manifest === 'string' ? safeJsonParse<any>(pkg.manifest) : pkg.manifest)
          : null;
        if (manifest?.ordered_stages && Array.isArray(manifest.ordered_stages)) {
          state.staging.resolvedStages = manifest.ordered_stages.map((s: any) => ({
            stage: s.stage,
            entityType: s.entityType || s.entity_type || '',
            items: Array.isArray(s.items) ? s.items : [],
          }));
        }
      }
    }

    renderStagingPackages();
    renderStagingImportTargetSelect();
  } catch (err: any) {
    console.warn('Failed to fetch staging packages:', err);
    showToast('Failed to load staging packages', 'error');
  } finally {
    state.staging.loading = false;
  }
}

/**
 * Create a new staging package / virtual target.
 */
export async function createStagingPackage(
  name: string,
  description?: string,
  scenario?: StagingScenario | string,
  options?: Record<string, unknown>
): Promise<StagingPackageSummary | null> {
  if (!name || !name.trim()) {
    showToast('Package name is required', 'warning');
    return null;
  }

  const selectedScenario = (scenario || state.staging.scenarioWizard.selectedScenario || 'company_setup') as StagingScenario;

  try {
    const res = await fetch('/api/v1/staging/packages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.trim(),
        description: description?.trim() || undefined,
        scenario: selectedScenario,
        manifest: options ? { scenario_options: options } : undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const pkg = data.package as StagingPackageSummary;
    state.staging.packages.unshift(pkg);
    showToast(`Virtual target "${pkg.name}" created`, 'success');
    await selectStagingPackage(pkg.id);

    if (options?.autoFetchSource) {
      await populateVirtualTargetFromScenario(pkg.id, selectedScenario, options);
    }

    return pkg;
  } catch (err: any) {
    console.warn('Create staging package failed:', err);
    showToast(err.message || 'Failed to create staging package', 'error');
    return null;
  }
}

/**
 * Populate a virtual target based on scenario presets and source connection.
 */
export async function populateVirtualTargetFromScenario(
  packageId: string,
  scenario: StagingScenario,
  options?: Record<string, unknown>
): Promise<void> {
  try {
    if (scenario === 'company_setup') {
      const entityTypes: string[] = [];
      if (options?.includeLocales !== false) entityTypes.push('locales', 'currencies');
      if (options?.includeChannels !== false) entityTypes.push('channels');
      if (options?.includeAttributes !== false) entityTypes.push('attribute_groups', 'attributes');
      if (options?.includeFamilies !== false) entityTypes.push('families');
      if (options?.includeCategories !== false) entityTypes.push('categories');
      if (options?.includeReferenceEntities !== false) entityTypes.push('reference_entities');
      if (options?.includeMeasurementFamilies !== false) entityTypes.push('measurement_families');

      const entityItems: Array<{ entity_type: string; entity_id: string }> = [];
      for (const type of entityTypes) {
        try {
          const res = await fetch(`/api/v1/discovery/explorer/${encodeURIComponent(type)}?limit=25`);
          if (res.ok) {
            const data = await res.json();
            const items = data.items || [];
            for (const item of items) {
              const id = item.code || item.identifier || item.id;
              if (id) entityItems.push({ entity_type: type, entity_id: String(id) });
            }
          }
        } catch {
          // Continue to next entity type
        }
      }

      if (entityItems.length > 0) {
        await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/populate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entities: entityItems,
            resolve_dependencies: true,
          }),
        });
        showToast(`Auto-populated ${entityItems.length} structure entities`, 'success');
        await fetchStagingItems(packageId);
      }
    } else if (scenario === 'product_batch') {
      const params = new URLSearchParams();
      if (options?.family) params.set('family', String(options.family));
      if (options?.category) params.set('category', String(options.category));
      params.set('limit', String(options?.limit || 50));

      const res = await fetch(`/api/v1/discovery/explorer/products?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        const items = (data.items || []) as Array<{ identifier?: string; id?: string }>;
        const productEntities = items
          .map((p) => ({ entity_type: 'products', entity_id: p.identifier || p.id || '' }))
          .filter((p) => Boolean(p.entity_id));

        if (productEntities.length > 0) {
          await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/populate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entities: productEntities,
              resolve_dependencies: options?.resolveDependencies !== false,
              include_media: options?.includeMedia !== false,
            }),
          });
          showToast(`Auto-populated ${productEntities.length} product entities`, 'success');
          await fetchStagingItems(packageId);
        }
      }
    } else if (scenario === 'backup_snapshot') {
      const scope = (options?.scope as string) || 'full';
      const entityItems: Array<{ entity_type: string; entity_id: string }> = [];

      if (options?.autoFetchSource) {
        if (scope === 'full' || scope === 'structure') {
          const structureTypes = [
            'locales',
            'currencies',
            'channels',
            'attribute_groups',
            'attributes',
            'families',
            'categories',
            'reference_entities',
            'measurement_families',
          ];
          for (const type of structureTypes) {
            try {
              const res = await fetch(`/api/v1/discovery/explorer/${encodeURIComponent(type)}?limit=50`);
              if (res.ok) {
                const data = await res.json();
                const items = data.items || [];
                for (const item of items) {
                  const id = item.code || item.identifier || item.id;
                  if (id) entityItems.push({ entity_type: type, entity_id: String(id) });
                }
              }
            } catch {
              // Continue
            }
          }
        }

        if (scope === 'full' || scope === 'catalog') {
          try {
            const res = await fetch('/api/v1/discovery/explorer/products?limit=50');
            if (res.ok) {
              const data = await res.json();
              const items = (data.items || []) as Array<{ identifier?: string; id?: string }>;
              for (const p of items) {
                const id = p.identifier || p.id;
                if (id) entityItems.push({ entity_type: 'products', entity_id: String(id) });
              }
            }
          } catch {
            // Continue
          }
        }

        if (entityItems.length > 0) {
          await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/populate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entities: entityItems,
              resolve_dependencies: true,
              include_media: options?.includeMedia !== false,
            }),
          });
          showToast(`Auto-populated ${entityItems.length} snapshot entities`, 'success');
          await fetchStagingItems(packageId);
        }
      }

      if (options?.autoFreeze) {
        await freezeStagingPackage();
      }
    }
  } catch (err: any) {
    console.warn('Auto-populate from scenario failed:', err);
  }
}

/**
 * Select a staging package and load its curated items.
 */
export async function selectStagingPackage(packageId: string | null): Promise<void> {
  state.staging.selectedPackageId = packageId;
  const pkg = state.staging.packages.find((p) => p.id === packageId) || null;
  state.staging.selectedPackage = pkg;

  const manifest = pkg?.manifest
    ? (typeof pkg.manifest === 'string' ? safeJsonParse<any>(pkg.manifest) : pkg.manifest)
    : null;

  if (manifest?.ordered_stages && Array.isArray(manifest.ordered_stages)) {
    state.staging.resolvedStages = manifest.ordered_stages.map((s: any) => ({
      stage: s.stage,
      entityType: s.entityType || s.entity_type || '',
      items: Array.isArray(s.items) ? s.items : [],
    }));
  } else {
    state.staging.resolvedStages = [];
  }

  state.staging.page = 1;
  state.staging.search = '';
  state.staging.filterType = 'all';
  state.staging.selectedItemIds.clear();
  closeStagingItemInspectModal();
  closeStagingBulkDeleteModal();

  if (packageId) {
    await fetchStagingItems(packageId);
  } else {
    state.staging.items = [];
  }

  renderStagingPackages();
  renderStagingItems();
  renderStagingGraph();
  updateStagingReleaseActionsUI();
}

/**
 * Fetch curated items for a staging package.
 */
export async function fetchStagingItems(packageId: string): Promise<void> {
  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/items`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.staging.items = (data.items || []) as StagingPackageItem[];
    renderStagingItems();
  } catch (err: any) {
    console.warn('Failed to fetch staging items:', err);
    showToast('Failed to load package items', 'error');
  }
}

/**
 * Add a curated entity to the selected package.
 */
export async function addStagingItem(entityType: string, entityId: string): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select or create a package first', 'warning');
    return;
  }
  if (!entityType || !entityId || !entityId.trim()) {
    showToast('Entity type and identifier are required', 'warning');
    return;
  }

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ entity_type: entityType, entity_id: entityId.trim() }],
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    showToast(`Added ${entityType}:${entityId} to package`, 'success');
    await fetchStagingItems(packageId);
  } catch (err: any) {
    console.warn('Add staging item failed:', err);
    showToast(err.message || 'Failed to add item to package', 'error');
  }
}

/**
 * Remove an item from the selected package.
 */
export async function removeStagingItem(itemId: string): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) return;

  try {
    const res = await fetch(`/api/v1/staging/items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    state.staging.selectedItemIds.delete(itemId);
    if (state.staging.inspectItem?.id === itemId) {
      closeStagingItemInspectModal();
    }
    showToast('Item removed from package', 'success');
    await fetchStagingItems(packageId);
  } catch (err: any) {
    console.warn('Remove staging item failed:', err);
    showToast(err.message || 'Failed to remove item', 'error');
  }
}

const RESOLVE_POLL_INTERVAL_MS = 250;

function stopResolvePolling(): void {
  if (state.staging.resolve.progressPollTimer) {
    clearInterval(state.staging.resolve.progressPollTimer);
    state.staging.resolve.progressPollTimer = null;
  }
}

function renderResolveProgress(): void {
  const progress = state.staging.resolve.progress;

  const curatePanel = document.getElementById('staging-resolve-progress-panel');
  const curateBar = document.getElementById('staging-resolve-progress-bar');
  const curateText = document.getElementById('staging-resolve-progress-text');
  if (curatePanel && curateBar && curateText) {
    curatePanel.style.display = 'block';
    curateBar.style.width = `${progress}%`;
    curateText.textContent = `Resolving dependencies... ${progress}%`;
  }

  const releasePanel = document.getElementById('staging-resolve-progress-panel-release');
  const releaseBar = document.getElementById('staging-resolve-progress-bar-release');
  const releaseText = document.getElementById('staging-resolve-progress-text-release');
  if (releasePanel && releaseBar && releaseText) {
    releasePanel.style.display = 'block';
    releaseBar.style.width = `${progress}%`;
    releaseText.textContent = `Resolving dependencies... ${progress}%`;
  }
}

function hideResolveProgress(): void {
  const curatePanel = document.getElementById('staging-resolve-progress-panel');
  if (curatePanel) curatePanel.style.display = 'none';

  const releasePanel = document.getElementById('staging-resolve-progress-panel-release');
  if (releasePanel) releasePanel.style.display = 'none';
}

async function pollResolveJob(jobId: string, packageId: string): Promise<void> {
  stopResolvePolling();

  state.staging.resolve.activeJobId = jobId;
  state.staging.resolve.isRunning = true;
  state.staging.resolve.isCompleted = false;
  state.staging.resolve.status = 'running';
  state.staging.resolve.progress = 0;
  renderResolveProgress();

  state.staging.resolve.progressPollTimer = setInterval(async () => {
    try {
      const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}`);
      if (!res.ok) return;
      const data = await res.json();
      const job = data.job;
      if (!job) return;

      const processed = Number(job.processed_entities || 0);
      const total = Number(job.total_entities || 1);
      const progress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
      state.staging.resolve.progress = progress;
      renderResolveProgress();

      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        stopResolvePolling();
        state.staging.resolve.isRunning = false;
        state.staging.resolve.isCompleted = true;
        state.staging.resolve.status = job.status === 'completed' ? 'completed' : 'failed';

        if (job.status === 'completed') {
          const details = typeof job.details === 'string' ? safeJsonParse(job.details) : job.details;
          const seedCount = details?.seed_count || 0;
          const resolvedCount = details?.resolved_count || 0;
          const missingCount = (details?.missing_on_source || []).length;
          state.staging.resolve.lastResult = { seedCount, resolvedCount, missingCount };

          if (details?.ordered_stages && Array.isArray(details.ordered_stages)) {
            state.staging.resolvedStages = details.ordered_stages.map((s: any) => ({
              stage: s.stage,
              entityType: s.entityType || s.entity_type || '',
              items: Array.isArray(s.items) ? s.items : [],
            }));
          }

          showToast(
            `Resolved ${seedCount} seeds into ${resolvedCount} entities${missingCount > 0 ? ` (${missingCount} missing)` : ''}`,
            missingCount > 0 ? 'warning' : 'success'
          );

          await fetchStagingItems(packageId);
          await fetchStagingPackages();
          renderStagingGraph();
        } else {
          showToast(job.error_message || 'Dependency resolution failed', 'error');
        }

        hideResolveProgress();
      }
    } catch (err: any) {
      console.warn('Resolve job poll failed:', err);
    }
  }, RESOLVE_POLL_INTERVAL_MS);
}

async function runResolveJob(packageId: string, strategy: 'lean' | 'full' = 'full'): Promise<void> {
  if (!packageId) {
    showToast('Select a package to resolve dependencies', 'warning');
    return;
  }

  state.staging.loading = true;
  stopResolvePolling();

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ strategy }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    if (!data.job_id) {
      throw new Error('Resolve endpoint did not return a job id');
    }

    showToast('Dependency resolution started', 'info');
    await pollResolveJob(data.job_id, packageId);
  } catch (err: any) {
    console.warn('Resolve dependencies failed:', err);
    showToast(err.message || 'Failed to resolve dependencies', 'error');
    state.staging.resolve.isRunning = false;
    state.staging.resolve.isCompleted = true;
    state.staging.resolve.status = 'failed';
    hideResolveProgress();
  } finally {
    state.staging.loading = false;
  }
}

/**
 * Resolve dependencies for a specific staging package and merge the results
 * into the curated item list. Shows a progress indicator and refreshes the UI
 * when the background job completes.
 */
export async function resolveDependencies(packageId: string): Promise<void> {
  await runResolveJob(packageId, 'full');
}

/**
 * Run the dependency auto-resolver for the selected package.
 */
export async function runStagingResolve(strategy: 'lean' | 'full' = 'full'): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to resolve dependencies', 'warning');
    return;
  }

  await runResolveJob(packageId, strategy);
}

/**
 * Freeze the selected package snapshot.
 */
export function updateStagingReleaseActionsUI(): void {
  const freezeBtn = document.getElementById('btn-staging-freeze');
  if (!freezeBtn) return;

  const pkg = state.staging.selectedPackage;
  const isFrozen = pkg?.status === 'ready';

  if (isFrozen) {
    freezeBtn.innerHTML = '🔓 Unfreeze Release';
    freezeBtn.className = 'btn btn-warning';
    freezeBtn.title = 'Unfreeze this package back to draft status for further curation';
  } else {
    freezeBtn.innerHTML = '❄️ Freeze Release';
    freezeBtn.className = 'btn btn-secondary';
    freezeBtn.title = 'Freeze and lock this curated snapshot for release';
  }
}

/**
 * Unfreeze (revert status to draft) the selected package snapshot.
 */
export async function unfreezeStagingPackage(): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to unfreeze', 'warning');
    return;
  }

  state.staging.loading = true;
  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/unfreeze`, {
      method: 'POST',
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    showToast('Package unfrozen (set to draft)', 'info');
    await fetchStagingPackages();
  } catch (err: any) {
    console.warn('Unfreeze staging package failed:', err);
    showToast(err.message || 'Failed to unfreeze package', 'error');
  } finally {
    state.staging.loading = false;
    updateStagingReleaseActionsUI();
  }
}

/**
 * Freeze the selected package snapshot (or unfreeze if already frozen).
 */
export async function freezeStagingPackage(): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  const pkg = state.staging.selectedPackage;
  if (!packageId) {
    showToast('Select a package to freeze', 'warning');
    return;
  }

  if (pkg?.status === 'ready') {
    await unfreezeStagingPackage();
    return;
  }

  state.staging.loading = true;
  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/freeze`, {
      method: 'POST',
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    showToast('Package frozen and release manifest generated', 'success');
    await fetchStagingPackages();
  } catch (err: any) {
    console.warn('Freeze staging package failed:', err);
    showToast(err.message || 'Failed to freeze package', 'error');
  } finally {
    state.staging.loading = false;
    updateStagingReleaseActionsUI();
  }
}

/**
 * Download the selected package as a .akeneopack archive.
 */
export function exportStagingPackage(): void {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to export', 'warning');
    return;
  }

  const pkg = state.staging.selectedPackage;
  const baseName = pkg?.name?.replace(/[^a-zA-Z0-9_-]/g, '_') || packageId;
  const url = `/api/v1/staging/packages/${encodeURIComponent(packageId)}/export`;

  const a = document.createElement('a');
  a.href = url;
  a.download = `${baseName}.akeneopack`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  showToast('Downloading .akeneopack archive', 'success');
}

/**
 * Open the Push to Target PIM deploy modal.
 */
export function openDeployStagingModal(): void {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to deploy', 'warning');
    return;
  }

  const pkg = state.staging.selectedPackage;
  const itemCount = state.staging.items.length;

  const packageBadge = document.getElementById('staging-deploy-modal-package-badge');
  if (packageBadge) {
    packageBadge.textContent = `${pkg?.name || packageId} (${itemCount} items)`;
  }

  // Switch to config view initially
  switchStagingDeployView('config');

  // Reset dry run preview panel
  state.staging.deploy.dryRunResult = null;
  renderStagingDryRunPreview(null);

  // Render target connections dropdown
  renderStagingTargetSelect();

  // Show modal
  const modal = document.getElementById('staging-deploy-modal');
  if (modal) modal.style.display = 'flex';

  // Automatically check readiness if a target connection is selected
  if (state.staging.deploy.targetConnectionId) {
    fetchStagingTargetReadiness(state.staging.deploy.targetConnectionId);
  } else {
    renderStagingReadinessScorecard(null);
  }
}

/**
 * Close the Push to Target PIM deploy modal.
 */
export function closeDeployStagingModal(): void {
  if (state.staging.deploy.progressPollTimer) {
    clearInterval(state.staging.deploy.progressPollTimer);
    state.staging.deploy.progressPollTimer = null;
  }
  if (state.staging.deploy.elapsedTimer) {
    clearInterval(state.staging.deploy.elapsedTimer);
    state.staging.deploy.elapsedTimer = null;
  }
  state.staging.deploy.isRunning = false;

  const modal = document.getElementById('staging-deploy-modal');
  if (modal) modal.style.display = 'none';
}

/**
 * Switch between deploy modal sub-views (config vs progress).
 */
export function switchStagingDeployView(view: 'config' | 'progress'): void {
  state.staging.deploy.activeView = view;
  const configSection = document.getElementById('staging-deploy-config-section');
  const progressSection = document.getElementById('staging-deploy-progress-section');
  const btnBack = document.getElementById('btn-back-staging-deploy-modal');
  const btnLogs = document.getElementById('btn-staging-deploy-view-logs');
  const btnCancel = document.getElementById('btn-cancel-staging-deploy-modal');
  const btnPreview = document.getElementById('btn-staging-deploy-dryrun-preview');
  const btnConfirm = document.getElementById('btn-confirm-staging-deploy-modal');
  const btnDone = document.getElementById('btn-done-staging-deploy-modal');
  const modeBadge = document.getElementById('staging-deploy-modal-mode-badge');

  if (configSection) configSection.style.display = view === 'config' ? 'block' : 'none';
  if (progressSection) progressSection.style.display = view === 'progress' ? 'block' : 'none';

  if (view === 'config') {
    if (btnBack) btnBack.style.display = 'none';
    if (btnLogs) btnLogs.style.display = 'none';
    if (btnCancel) btnCancel.style.display = 'inline-block';
    if (btnPreview) btnPreview.style.display = 'inline-block';
    if (btnConfirm) btnConfirm.style.display = 'inline-block';
    if (btnDone) btnDone.style.display = 'none';
    if (modeBadge) modeBadge.style.display = 'none';
  } else {
    if (btnBack) btnBack.style.display = state.staging.deploy.isRunning ? 'none' : 'inline-block';
    if (btnLogs) btnLogs.style.display = state.staging.deploy.isCompleted ? 'inline-block' : 'none';
    if (btnCancel) btnCancel.style.display = state.staging.deploy.isCompleted ? 'none' : 'inline-block';
    if (btnPreview) btnPreview.style.display = 'none';
    if (btnConfirm) btnConfirm.style.display = state.staging.deploy.isCompleted ? 'none' : 'inline-block';
    if (btnDone) btnDone.style.display = state.staging.deploy.isCompleted ? 'inline-block' : 'none';
    if (modeBadge) {
      modeBadge.style.display = 'inline-block';
      modeBadge.textContent = state.staging.deploy.dryRun ? 'Dry Run' : 'Live Deploy';
      modeBadge.className = state.staging.deploy.dryRun ? 'badge badge-warning' : 'badge badge-primary';
    }
  }
}

/**
 * Fetch and evaluate Target Readiness Pre-Flight Scorecard.
 */
export async function fetchStagingTargetReadiness(
  targetConnectionId?: string
): Promise<TargetReadinessResult | null> {
  const connId = targetConnectionId || state.staging.deploy.targetConnectionId;
  if (!connId) {
    renderStagingReadinessScorecard(null);
    return null;
  }

  state.staging.deploy.readinessLoading = true;
  const statusBadge = document.getElementById('staging-deploy-readiness-badge');
  if (statusBadge) {
    statusBadge.className = 'badge badge-neutral';
    statusBadge.textContent = 'Scanning...';
  }

  try {
    const res = await fetch(`/api/v1/sync/readiness?target_connection_id=${encodeURIComponent(connId)}`);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    const readiness: TargetReadinessResult = data.readiness;
    state.staging.deploy.readiness = readiness;
    renderStagingReadinessScorecard(readiness);
    return readiness;
  } catch (err: any) {
    console.warn('Readiness check failed:', err);
    const fallbackReadiness: TargetReadinessResult = {
      status: 'warning',
      score: 75,
      checks: [
        {
          id: 'conn_warn',
          name: 'Connection Check',
          category: 'connection',
          status: 'warning',
          message: err.message || 'Target connection responded with warning',
        },
      ],
      enabledLocales: [],
      enabledCurrencies: [],
      missingLocales: [],
      missingCurrencies: [],
      recommendations: [err.message || 'Unable to complete full pre-flight scan'],
    };
    state.staging.deploy.readiness = fallbackReadiness;
    renderStagingReadinessScorecard(fallbackReadiness);
    return fallbackReadiness;
  } finally {
    state.staging.deploy.readinessLoading = false;
  }
}

/**
 * Render Target Readiness Pre-Flight Scorecard UI.
 */
export function renderStagingReadinessScorecard(
  readiness?: TargetReadinessResult | null
): void {
  const badgeEl = document.getElementById('staging-deploy-readiness-badge');
  const scoreEl = document.getElementById('staging-deploy-readiness-score');
  const hostEl = document.getElementById('staging-deploy-readiness-host');
  const warningsEl = document.getElementById('staging-deploy-readiness-warnings');
  const recListEl = document.getElementById('staging-deploy-readiness-recommendations-list');
  const checkAuth = document.getElementById('readiness-check-auth');
  const checkLocales = document.getElementById('readiness-check-locales');
  const checkCurrencies = document.getElementById('readiness-check-currencies');
  const checkSchema = document.getElementById('readiness-check-schema');

  if (!readiness) {
    if (badgeEl) {
      badgeEl.className = 'badge badge-neutral';
      badgeEl.textContent = 'Awaiting Target';
    }
    if (scoreEl) scoreEl.textContent = 'Score: --';
    if (hostEl) hostEl.textContent = 'Select a target connection to evaluate environment readiness.';
    if (warningsEl) warningsEl.style.display = 'none';

    [checkAuth, checkLocales, checkCurrencies, checkSchema].forEach((el) => {
      if (el) {
        el.className = 'readiness-check-item';
        const icon = el.querySelector('.check-icon');
        if (icon) icon.textContent = '⚪';
      }
    });
    return;
  }

  // 1. Status badge & score
  const status = readiness.status || 'ready';
  const score = typeof readiness.score === 'number' ? readiness.score : (status === 'ready' ? 100 : status === 'warning' ? 80 : 40);

  if (badgeEl) {
    if (status === 'ready') {
      badgeEl.className = 'badge badge-success';
      badgeEl.textContent = 'Ready to Deploy';
    } else if (status === 'warning') {
      badgeEl.className = 'badge badge-warning';
      badgeEl.textContent = 'Warnings Detected';
    } else {
      badgeEl.className = 'badge badge-danger';
      badgeEl.textContent = 'Not Ready';
    }
  }

  if (scoreEl) {
    scoreEl.textContent = `Score: ${score}%`;
    scoreEl.style.color = status === 'ready' ? 'var(--color-success, #22c55e)' : status === 'warning' ? 'var(--color-warning, #f59e0b)' : 'var(--color-danger, #ef4444)';
  }

  // 2. Target Host info
  const targetConn = state.connections.list.find((c) => c.id === state.staging.deploy.targetConnectionId) || state.connections.target;
  if (hostEl) {
    const hostName = readiness.serverInfo?.host || targetConn?.name || 'Target PIM';
    const hostUrl = targetConn?.url ? ` (${targetConn.url})` : '';
    hostEl.innerHTML = `<strong>Target Instance:</strong> ${escapeHtml(hostName)}${escapeHtml(hostUrl)} • Pre-flight checks passed`;
  }

  // 3. Checks grid
  const updateCheckItem = (el: HTMLElement | null, pass: boolean, warn: boolean, text: string) => {
    if (!el) return;
    const cls = pass ? 'check-pass' : warn ? 'check-warning' : 'check-fail';
    el.className = `readiness-check-item ${cls}`;
    const icon = el.querySelector('.check-icon');
    const textEl = el.querySelector('.check-text');
    if (icon) icon.textContent = pass ? '✅' : warn ? '⚠️' : '❌';
    if (textEl) textEl.textContent = text;
  };

  const hasMissingLocales = readiness.missingLocales && readiness.missingLocales.length > 0;
  const hasMissingCurrencies = readiness.missingCurrencies && readiness.missingCurrencies.length > 0;

  updateCheckItem(
    checkAuth,
    status !== 'not_ready',
    false,
    'Connection & OAuth Authentication Verified'
  );

  updateCheckItem(
    checkLocales,
    !hasMissingLocales,
    Boolean(hasMissingLocales),
    hasMissingLocales
      ? `Missing Locales: ${readiness.missingLocales.join(', ')}`
      : `Locales Supported: ${readiness.enabledLocales?.slice(0, 3).join(', ') || 'OK'}`
  );

  updateCheckItem(
    checkCurrencies,
    !hasMissingCurrencies,
    Boolean(hasMissingCurrencies),
    hasMissingCurrencies
      ? `Missing Currencies: ${readiness.missingCurrencies.join(', ')}`
      : `Currencies Supported: ${readiness.enabledCurrencies?.slice(0, 3).join(', ') || 'OK'}`
  );

  updateCheckItem(
    checkSchema,
    true,
    false,
    'Target Schema & Permissions Compatible'
  );

  // 4. Recommendations / Warnings
  if (warningsEl && recListEl) {
    const recs = readiness.recommendations || [];
    if (recs.length > 0) {
      warningsEl.style.display = 'block';
      recListEl.innerHTML = recs.map((r) => `<li>${escapeHtml(r)}</li>`).join('');
    } else {
      warningsEl.style.display = 'none';
      recListEl.innerHTML = '';
    }
  }
}

/**
 * Execute dry-run deployment simulation and display diff preview breakdown.
 */
export async function runStagingDryRunPreview(): Promise<StagingDeployResult | null> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to preview', 'warning');
    return null;
  }

  const targetConnectionId = state.staging.deploy.targetConnectionId;
  if (!targetConnectionId) {
    showToast('Select a target connection', 'warning');
    return null;
  }

  state.staging.deploy.dryRunLoading = true;
  const btnPreview = document.getElementById('btn-staging-deploy-dryrun-preview');
  if (btnPreview) {
    btnPreview.setAttribute('disabled', 'true');
    btnPreview.textContent = '⏳ Simulating...';
  }

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/deploy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_connection_id: targetConnectionId,
        dry_run: true,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const deployment: StagingDeployResult = data.deployment;
    state.staging.deploy.dryRunResult = deployment;
    renderStagingDryRunPreview(deployment);
    showToast('Dry-run preview simulation generated', 'success');
    return deployment;
  } catch (err: any) {
    console.warn('Dry-run preview failed:', err);
    showToast(err.message || 'Failed to execute dry-run preview', 'error');
    return null;
  } finally {
    state.staging.deploy.dryRunLoading = false;
    if (btnPreview) {
      btnPreview.removeAttribute('disabled');
      btnPreview.textContent = 'Run Dry-Run Preview';
    }
  }
}

/**
 * Render Dry-Run Diff Preview breakdown and stages.
 */
export function renderStagingDryRunPreview(
  result?: StagingDeployResult | null
): void {
  const panel = document.getElementById('staging-deploy-dryrun-preview-panel');
  if (!panel) return;

  if (!result) {
    panel.style.display = 'none';
    return;
  }

  panel.style.display = 'block';

  const statusBadge = document.getElementById('staging-deploy-dryrun-status-badge');
  const metricTotal = document.getElementById('staging-dryrun-metric-total');
  const metricCreated = document.getElementById('staging-dryrun-metric-created');
  const metricUpdated = document.getElementById('staging-dryrun-metric-updated');
  const metricUnchanged = document.getElementById('staging-dryrun-metric-unchanged');
  const metricErrors = document.getElementById('staging-dryrun-metric-errors');
  const stagesList = document.getElementById('staging-deploy-dryrun-stages-list');
  const errorsAlert = document.getElementById('staging-deploy-dryrun-errors-alert');
  const errorsList = document.getElementById('staging-deploy-dryrun-errors-list');

  const metrics = result.metrics || {
    total: state.staging.items.length,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    processed: 0,
  };

  const hasErrors = (result.errors && result.errors.length > 0) || (metrics.failed && metrics.failed > 0);

  if (statusBadge) {
    if (hasErrors) {
      statusBadge.className = 'badge badge-danger';
      statusBadge.textContent = 'Simulation Has Errors';
    } else {
      statusBadge.className = 'badge badge-success';
      statusBadge.textContent = 'Simulation Succeeded (Zero Mutations)';
    }
  }

  if (metricTotal) metricTotal.textContent = String(metrics.total || state.staging.items.length);
  if (metricCreated) metricCreated.textContent = String(metrics.created || 0);
  if (metricUpdated) metricUpdated.textContent = String(metrics.updated || 0);
  if (metricUnchanged) metricUnchanged.textContent = String(metrics.skipped || 0);
  if (metricErrors) metricErrors.textContent = String(result.errors?.length || metrics.failed || 0);

  // Render Stage Breakdown
  if (stagesList) {
    const executionOrder = result.executionOrder || Object.keys(result.stages || {});
    if (executionOrder.length === 0) {
      stagesList.innerHTML = `
        <div class="text-muted" style="padding: 0.5rem 0.75rem; font-size: 0.8rem;">
          No execution stages recorded.
        </div>
      `;
    } else {
      let stagesHtml = '';
      executionOrder.forEach((stageKey, idx) => {
        const stageData = result.stages?.[stageKey];
        const typeInfo = STAGING_ENTITY_TYPES.find((t) => t.key === stageKey);
        const stageIcon = typeInfo?.icon || '📦';
        const stageLabel = typeInfo?.label || stageKey;

        const created = stageData?.metrics?.created || 0;
        const updated = stageData?.metrics?.updated || 0;
        const skipped = stageData?.metrics?.skipped || 0;
        const total = stageData?.metrics?.total || (created + updated + skipped) || 0;

        stagesHtml += `
          <div class="dryrun-stage-row">
            <div class="dryrun-stage-info">
              <span class="badge badge-neutral" style="font-size: 0.7rem;">${idx + 1}</span>
              <span>${stageIcon} <strong>${escapeHtml(stageLabel)}</strong></span>
              <span class="text-muted" style="font-size: 0.75rem;">(${total} items)</span>
            </div>
            <div class="dryrun-stage-badges">
              ${created > 0 ? `<span class="badge badge-success" style="font-size: 0.7rem;">+${created} Create</span>` : ''}
              ${updated > 0 ? `<span class="badge badge-info" style="font-size: 0.7rem;">~${updated} Update</span>` : ''}
              ${skipped > 0 ? `<span class="badge badge-neutral" style="font-size: 0.7rem;">=${skipped} Skip</span>` : ''}
              <span class="badge badge-accent" style="font-size: 0.7rem;">Valid</span>
            </div>
          </div>
        `;
      });
      stagesList.innerHTML = stagesHtml;
    }
  }

  // Render Errors if any
  if (errorsAlert && errorsList) {
    const errs = result.errors || [];
    if (errs.length > 0) {
      errorsAlert.style.display = 'block';
      errorsList.innerHTML = errs
        .map(
          (e) =>
            `<li><strong>${escapeHtml(e.entityType || 'Entity')}${e.entityId ? ` [${escapeHtml(e.entityId)}]` : ''}:</strong> ${escapeHtml(e.message || 'Validation error')}</li>`
        )
        .join('');
    } else {
      errorsAlert.style.display = 'none';
      errorsList.innerHTML = '';
    }
  }
}

/**
 * Deploy the selected package to a target PIM (Live or Dry-Run) with live progress tracking.
 */
export async function deployStagingPackage(options?: { dryRun?: boolean }): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  if (!packageId) {
    showToast('Select a package to deploy', 'warning');
    return;
  }

  const targetConnectionId = state.staging.deploy.targetConnectionId;
  if (!targetConnectionId) {
    showToast('Select a target connection', 'warning');
    return;
  }

  const isDryRun = options?.dryRun !== undefined ? options.dryRun : state.staging.deploy.dryRun;
  state.staging.deploy.dryRun = isDryRun;
  state.staging.deploy.isRunning = true;
  state.staging.deploy.isCompleted = false;
  state.staging.deploy.status = 'running';
  state.staging.deploy.startTime = Date.now();
  state.staging.deploy.elapsedSeconds = 0;
  state.staging.deploy.logs = [];

  // Switch to progress view
  switchStagingDeployView('progress');

  // Reset UI elements in progress view
  const bar = document.getElementById('staging-deploy-progress-bar');
  const percentEl = document.getElementById('staging-deploy-progress-percent');
  const stageEl = document.getElementById('staging-deploy-stage-label');
  const countsEl = document.getElementById('staging-deploy-counts-text');
  const elapsedEl = document.getElementById('staging-deploy-elapsed-text');
  const createdEl = document.getElementById('staging-deploy-metric-created');
  const updatedEl = document.getElementById('staging-deploy-metric-updated');
  const unchangedEl = document.getElementById('staging-deploy-metric-unchanged');
  const failedEl = document.getElementById('staging-deploy-metric-failed');
  const failedAlert = document.getElementById('staging-deploy-failed-alert');
  const resultBanner = document.getElementById('staging-deploy-result-banner');
  const logsList = document.getElementById('staging-deploy-logs-list');
  const logsCount = document.getElementById('staging-deploy-logs-count');

  if (bar) bar.style.width = '5%';
  if (percentEl) percentEl.textContent = '5%';
  if (stageEl) stageEl.textContent = isDryRun ? 'Initializing dry-run simulation...' : 'Initializing deployment pipeline...';
  if (countsEl) countsEl.textContent = `0 / ${state.staging.items.length} entities processed`;
  if (elapsedEl) elapsedEl.textContent = '0s elapsed';
  if (createdEl) createdEl.textContent = '0';
  if (updatedEl) updatedEl.textContent = '0';
  if (unchangedEl) unchangedEl.textContent = '0';
  if (failedEl) failedEl.textContent = '0';
  if (failedAlert) failedAlert.style.display = 'none';
  if (resultBanner) resultBanner.style.display = 'none';

  if (logsList) {
    logsList.innerHTML = `
      <div class="audit-log-item log-info">
        <span class="log-time">[${new Date().toLocaleTimeString()}]</span>
        <span class="log-message">Starting deployment of package '${escapeHtml(packageId)}' to target '${escapeHtml(targetConnectionId)}' (dryRun=${isDryRun})...</span>
      </div>
    `;
  }
  if (logsCount) logsCount.textContent = '1 logs';

  // Start elapsed timer
  if (state.staging.deploy.elapsedTimer) clearInterval(state.staging.deploy.elapsedTimer);
  state.staging.deploy.elapsedTimer = setInterval(() => {
    if (!state.staging.deploy.startTime) return;
    const elapsed = Math.max(0, Math.floor((Date.now() - state.staging.deploy.startTime) / 1000));
    state.staging.deploy.elapsedSeconds = elapsed;
    const el = document.getElementById('staging-deploy-elapsed-text');
    if (el) el.textContent = `${elapsed}s elapsed`;
  }, 1000);

  state.staging.loading = true;
  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/deploy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_connection_id: targetConnectionId,
        dry_run: isDryRun,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const deployment: StagingDeployResult = data.deployment;
    if (deployment) {
      state.staging.deploy.activeJobId = deployment.jobId || null;
      updateStagingDeployProgress(deployment);

      // Append deployment audit logs if any
      if (deployment.auditLogs && Array.isArray(deployment.auditLogs)) {
        deployment.auditLogs.forEach((log) => {
          addStagingDeployLog({
            level: log.level || 'info',
            message: log.message || 'Stage execution log',
            timestamp: log.created_at || new Date().toISOString(),
          });
        });
      }
    }

    // Stop elapsed timer
    if (state.staging.deploy.elapsedTimer) {
      clearInterval(state.staging.deploy.elapsedTimer);
      state.staging.deploy.elapsedTimer = null;
    }

    state.staging.deploy.isRunning = false;
    state.staging.deploy.isCompleted = true;
    state.staging.deploy.status = deployment?.status === 'failed' ? 'failed' : 'completed';

    // Update buttons in footer
    switchStagingDeployView('progress');

    showToast(
      isDryRun
        ? 'Dry-run deploy completed'
        : 'Package pushed to target PIM',
      'success'
    );

    if (!isDryRun) {
      await fetchStagingPackages();
    }
  } catch (err: any) {
    console.warn('Deploy staging package failed:', err);
    if (state.staging.deploy.elapsedTimer) {
      clearInterval(state.staging.deploy.elapsedTimer);
      state.staging.deploy.elapsedTimer = null;
    }

    state.staging.deploy.isRunning = false;
    state.staging.deploy.isCompleted = true;
    state.staging.deploy.status = 'failed';

    if (stageEl) stageEl.textContent = `Deploy failed: ${err.message || 'Execution error'}`;
    if (failedEl) failedEl.textContent = '1';

    addStagingDeployLog({
      level: 'error',
      message: `Deploy failed: ${err.message || 'Unknown error'}`,
      timestamp: new Date().toISOString(),
    });

    if (resultBanner) {
      resultBanner.className = 'alert alert-danger';
      resultBanner.style.display = 'block';
      const titleEl = document.getElementById('staging-deploy-result-title');
      const msgEl = document.getElementById('staging-deploy-result-message');
      if (titleEl) titleEl.textContent = '❌ Deployment Failed';
      if (msgEl) msgEl.textContent = err.message || 'Target instance rejected deployment request.';
    }

    switchStagingDeployView('progress');
    showToast(err.message || 'Failed to deploy package', 'error');
  } finally {
    state.staging.loading = false;
  }
}

/**
 * Update deployment progress modal with live job metrics and stages.
 */
export function updateStagingDeployProgress(jobOrDeployment: any): void {
  if (!jobOrDeployment) return;

  const bar = document.getElementById('staging-deploy-progress-bar');
  const percentEl = document.getElementById('staging-deploy-progress-percent');
  const stageEl = document.getElementById('staging-deploy-stage-label');
  const countsEl = document.getElementById('staging-deploy-counts-text');
  const createdEl = document.getElementById('staging-deploy-metric-created');
  const updatedEl = document.getElementById('staging-deploy-metric-updated');
  const unchangedEl = document.getElementById('staging-deploy-metric-unchanged');
  const failedEl = document.getElementById('staging-deploy-metric-failed');
  const failedAlert = document.getElementById('staging-deploy-failed-alert');
  const failedTitle = document.getElementById('staging-deploy-failed-title');
  const failedSubtitle = document.getElementById('staging-deploy-failed-subtitle');
  const resultBanner = document.getElementById('staging-deploy-result-banner');
  const resultTitle = document.getElementById('staging-deploy-result-title');
  const resultMessage = document.getElementById('staging-deploy-result-message');

  const total =
    jobOrDeployment.total_entities ||
    jobOrDeployment.metrics?.total ||
    jobOrDeployment.progress?.totalEntities ||
    state.staging.items.length ||
    1;
  const processed =
    jobOrDeployment.processed_entities ??
    jobOrDeployment.metrics?.processed ??
    jobOrDeployment.progress?.processedEntities ??
    (jobOrDeployment.status === 'completed' ? total : 0);
  const percent =
    total > 0
      ? Math.min(100, Math.round((processed / total) * 100))
      : (jobOrDeployment.status === 'completed' ? 100 : 10);

  if (bar) bar.style.width = `${percent}%`;
  if (percentEl) percentEl.textContent = `${percent}%`;
  if (countsEl) countsEl.textContent = `${processed} / ${total} entities processed`;

  const status = jobOrDeployment.status || (jobOrDeployment.success ? 'completed' : 'running');

  if (stageEl) {
    if (status === 'completed') {
      stageEl.textContent = jobOrDeployment.dryRun
        ? 'Dry-run simulation finished successfully.'
        : 'All deployment stages completed successfully.';
    } else if (status === 'failed') {
      stageEl.textContent = `Deployment failed: ${jobOrDeployment.error || 'Execution error'}`;
    } else {
      stageEl.textContent = jobOrDeployment.progress?.currentStage
        ? `Processing Stage: ${jobOrDeployment.progress.currentStage}`
        : 'Executing topological deployment stages...';
    }
  }

  const created =
    jobOrDeployment.created_count ??
    jobOrDeployment.metrics?.created ??
    jobOrDeployment.progress?.createdCount ??
    0;
  const updated =
    jobOrDeployment.updated_count ??
    jobOrDeployment.metrics?.updated ??
    jobOrDeployment.progress?.updatedCount ??
    0;
  const unchanged =
    jobOrDeployment.skipped_count ??
    jobOrDeployment.metrics?.skipped ??
    jobOrDeployment.progress?.skippedCount ??
    0;
  const failed =
    jobOrDeployment.failed_count ??
    jobOrDeployment.metrics?.failed ??
    jobOrDeployment.progress?.failedCount ??
    jobOrDeployment.errors?.length ??
    0;

  if (createdEl) createdEl.textContent = Number(created).toLocaleString();
  if (updatedEl) updatedEl.textContent = Number(updated).toLocaleString();
  if (unchangedEl) unchangedEl.textContent = Number(unchanged).toLocaleString();
  if (failedEl) failedEl.textContent = Number(failed).toLocaleString();

  if (failedAlert) {
    if (failed > 0) {
      failedAlert.style.display = 'flex';
      if (failedTitle) failedTitle.textContent = `⚠️ ${failed} Failed Items Detected`;
      if (failedSubtitle)
        failedSubtitle.textContent = `Target Akeneo instance reported ${failed} errors during deployment.`;
    } else {
      failedAlert.style.display = 'none';
    }
  }

  if (resultBanner && (status === 'completed' || status === 'failed' || status === 'partial')) {
    resultBanner.style.display = 'block';
    if (status === 'completed') {
      resultBanner.className = 'alert alert-success';
      if (resultTitle)
        resultTitle.textContent = jobOrDeployment.dryRun
          ? '✅ Dry-Run Simulation Completed'
          : '🎉 Package Deployed Successfully';
      if (resultMessage)
        resultMessage.textContent = jobOrDeployment.dryRun
          ? `Simulation completed with zero target mutations. ${created} to create, ${updated} to update, ${unchanged} unchanged.`
          : `Deployment succeeded. ${created} created, ${updated} updated in target PIM.`;
    } else {
      resultBanner.className = 'alert alert-danger';
      if (resultTitle) resultTitle.textContent = '⚠️ Deployment Completed with Issues';
      if (resultMessage)
        resultMessage.textContent = `${failed} entities failed to sync. Review the execution logs below.`;
    }
  }
}

/**
 * Add a log item to the staging deploy modal live execution logs viewer.
 */
export function addStagingDeployLog(log: {
  level: string;
  message: string;
  timestamp?: string;
  details?: any;
}): void {
  if (!log || !log.message) return;

  const logsList = document.getElementById('staging-deploy-logs-list');
  const logsCount = document.getElementById('staging-deploy-logs-count');
  const autoscrollToggle = document.getElementById('staging-deploy-autoscroll-toggle') as HTMLInputElement | null;

  state.staging.deploy.logs.push({
    level: log.level || 'info',
    message: log.message,
    timestamp: log.timestamp || new Date().toISOString(),
    details: log.details,
  });

  if (logsCount) {
    logsCount.textContent = `${state.staging.deploy.logs.length} logs`;
  }

  if (logsList) {
    const item = document.createElement('div');
    item.className = `audit-log-item log-${escapeHtml(log.level || 'info')}`;
    const timeStr = log.timestamp
      ? new Date(log.timestamp).toLocaleTimeString()
      : new Date().toLocaleTimeString();
    item.innerHTML = `
      <span class="log-time">[${timeStr}]</span>
      <span class="log-message">${escapeHtml(log.message)}</span>
    `;
    logsList.appendChild(item);

    if (autoscrollToggle && autoscrollToggle.checked) {
      logsList.scrollTop = logsList.scrollHeight;
    }
  }
}

/**
 * Update a curate form field in state.
 */
export function setStagingCurateField(field: keyof StagingCurateForm, value: string): void {
  state.staging.curate[field] = value;
}

/**
 * Update a deploy form field in state.
 */
export function setStagingDeployField(
  field: keyof StagingDeployForm,
  value: any
): void {
  (state.staging.deploy[field] as any) = value;

  if (field === 'targetConnectionId') {
    if (value) {
      fetchStagingTargetReadiness(String(value));
    } else {
      renderStagingReadinessScorecard(null);
    }
  }
}

/**
 * Switch between staging sub-views.
 */
export function switchStagingSubTab(
  subTab: 'packages' | 'curate' | 'release'
): void {
  const valid = ['packages', 'curate', 'release'];
  if (!valid.includes(subTab)) subTab = 'packages';
  state.staging.activeSubTab = subTab;

  document.querySelectorAll('#staging-view-container .subtab-btn').forEach((btn) => {
    const isTarget = btn.getAttribute('data-subtab') === subTab;
    btn.classList.toggle('active', isTarget);
    btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
  });

  const packagesView = document.getElementById('staging-packages-view');
  const curateView = document.getElementById('staging-curate-view');
  const releaseView = document.getElementById('staging-release-view');

  if (packagesView) packagesView.style.display = subTab === 'packages' ? 'block' : 'none';
  if (curateView) curateView.style.display = subTab === 'curate' ? 'block' : 'none';
  if (releaseView) releaseView.style.display = subTab === 'release' ? 'block' : 'none';

  if (subTab === 'curate') {
    renderStagingItems();
  } else if (subTab === 'release') {
    renderStagingGraph();
    updateStagingReleaseActionsUI();
  }
}

/**
 * Render the target connection selector in the deploy modal.
 */
export function renderStagingTargetSelect(): void {
  const select = document.getElementById('staging-deploy-target') as HTMLSelectElement | null;
  if (!select) return;

  const targets = state.connections.list.filter((c) => c.type === 'target');
  const activeTarget = state.connections.target;

  let html = '<option value="">Select target connection…</option>';
  for (const conn of targets) {
    const label = `${conn.name} (${conn.url})`;
    html += `<option value="${escapeHtml(conn.id)}">${escapeHtml(label)}</option>`;
  }
  select.innerHTML = html;

  if (state.staging.deploy.targetConnectionId) {
    select.value = state.staging.deploy.targetConnectionId;
  } else if (activeTarget) {
    select.value = activeTarget.id;
    state.staging.deploy.targetConnectionId = activeTarget.id;
  } else if (targets.length > 0) {
    select.value = targets[0].id;
    state.staging.deploy.targetConnectionId = targets[0].id;
  }
}

export function openEditStagingPackageModal(packageId: string): void {
  const modal = document.getElementById('staging-edit-package-modal');
  const idInput = document.getElementById('staging-edit-package-id') as HTMLInputElement | null;
  const nameInput = document.getElementById('staging-edit-package-name') as HTMLInputElement | null;
  const scenarioSelect = document.getElementById('staging-edit-package-scenario') as HTMLSelectElement | null;
  const descInput = document.getElementById('staging-edit-package-description') as HTMLTextAreaElement | null;
  const createdEl = document.getElementById('staging-edit-package-created-at');
  const updatedEl = document.getElementById('staging-edit-package-updated-at');

  if (!modal) return;

  const pkg = state.staging.packages.find((p) => p.id === packageId);
  if (!pkg) {
    showToast('Staging package not found', 'error');
    return;
  }

  if (idInput) idInput.value = pkg.id;
  if (nameInput) nameInput.value = pkg.name || '';
  if (scenarioSelect) scenarioSelect.value = (pkg.scenario as string) || 'custom';
  if (descInput) descInput.value = pkg.description || '';
  if (createdEl) createdEl.textContent = pkg.created_at ? formatDateTime(pkg.created_at) : '—';
  if (updatedEl) updatedEl.textContent = pkg.updated_at ? formatDateTime(pkg.updated_at) : '—';

  modal.style.display = 'flex';
}

export function closeEditStagingPackageModal(): void {
  const modal = document.getElementById('staging-edit-package-modal');
  if (modal) modal.style.display = 'none';
}

export async function saveEditStagingPackage(): Promise<void> {
  const idInput = document.getElementById('staging-edit-package-id') as HTMLInputElement | null;
  const nameInput = document.getElementById('staging-edit-package-name') as HTMLInputElement | null;
  const scenarioSelect = document.getElementById('staging-edit-package-scenario') as HTMLSelectElement | null;
  const descInput = document.getElementById('staging-edit-package-description') as HTMLTextAreaElement | null;
  const confirmBtn = document.getElementById('btn-confirm-staging-edit-package-modal') as HTMLButtonElement | null;

  const packageId = idInput?.value;
  if (!packageId) return;

  const name = nameInput?.value?.trim() || '';
  if (!name) {
    showToast('Package name is required', 'warning');
    return;
  }

  const scenario = scenarioSelect?.value || 'custom';
  const description = descInput?.value?.trim() || undefined;

  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.innerHTML = '<span class="spinner-sm"></span> Saving...';
  }

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scenario, description }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    showToast('Package metadata updated', 'success');
    closeEditStagingPackageModal();
    await fetchStagingPackages();
  } catch (err: any) {
    console.warn('Failed to update package metadata:', err);
    showToast(`Update failed: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.innerHTML = 'Save Metadata';
    }
  }
}

/**
 * Render the staging packages list.
 */
export function renderStagingPackages(): void {
  const container = document.getElementById('staging-packages-list');
  const selectedBadge = document.getElementById('staging-selected-package-name');
  const releaseSelectedBadge = document.getElementById('staging-release-selected-package-name');
  const countBadge = document.getElementById('staging-packages-count');
  if (!container) return;

  const filter = state.staging.scenarioFilter || 'all';
  const allPackages = state.staging.packages || [];
  if (countBadge) {
    countBadge.textContent = String(allPackages.length);
  }

  // Update scenario filter tab active states
  const filterBtns = document.querySelectorAll<HTMLElement>('#staging-package-scenario-filters .filter-tab-btn');
  filterBtns.forEach((btn) => {
    const scenarioAttr = btn.getAttribute('data-scenario-filter') || 'all';
    btn.classList.toggle('active', scenarioAttr === filter);
  });

  const packages = filter === 'all'
    ? allPackages
    : allPackages.filter((pkg) => (pkg.scenario || 'custom') === filter);

  const selectedId = state.staging.selectedPackageId;
  const selectedName = state.staging.selectedPackage?.name || 'None selected';

  if (selectedBadge) {
    selectedBadge.textContent = selectedName;
  }
  if (releaseSelectedBadge) {
    releaseSelectedBadge.textContent = selectedName;
  }

  if (!packages.length) {
    container.innerHTML = `
      <div class="empty-state-inline text-muted">
        ${filter === 'all' ? 'No staging packages yet. Create a new package to start curating entities.' : `No staging packages matching scenario "${filter}".`}
      </div>
    `;
    return;
  }

  container.innerHTML = packages
    .map((pkg) => {
      const isSelected = pkg.id === selectedId;
      const createdFormatted = pkg.created_at ? formatDateTime(pkg.created_at) : '—';
      const updatedFormatted = pkg.updated_at ? formatDateTime(pkg.updated_at) : '—';
      const updatedRelative = pkg.updated_at ? formatRelativeTime(pkg.updated_at) : '';
      const scenarioPreset = SCENARIO_PRESETS[(pkg.scenario as StagingScenario) || 'custom'] || SCENARIO_PRESETS.custom;
      return `
        <div class="staging-package-card ${isSelected ? 'selected' : ''}" data-id="${escapeHtml(
          pkg.id
        )}" data-scenario="${escapeHtml(pkg.scenario || 'custom')}">
          <div class="staging-package-header">
            <span class="staging-package-name">${escapeHtml(pkg.name)}</span>
            <div style="display: flex; gap: 0.25rem; align-items: center;">
              <span class="badge ${scenarioPreset.badgeClass || 'badge-neutral'}">${scenarioPreset.icon} ${escapeHtml(scenarioPreset.shortLabel)}</span>
              <span class="badge badge-${pkg.status === 'ready' ? 'success' : pkg.status === 'applied' ? 'accent' : 'neutral'}">${escapeHtml(pkg.status)}</span>
            </div>
          </div>
          <div class="staging-package-meta" style="margin-bottom: 0.5rem;">
            <div style="font-size: 0.775rem; color: var(--text-secondary); display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 0.35rem;">
              <span>📅 Created: <strong class="mono">${escapeHtml(createdFormatted)}</strong></span>
              <span>🕒 Updated: <strong class="mono">${escapeHtml(updatedFormatted)}</strong> ${updatedRelative ? `(${escapeHtml(updatedRelative)})` : ''}</span>
            </div>
            ${pkg.description ? `<div style="font-size: 0.825rem; color: var(--text-muted); line-height: 1.4;">${escapeHtml(pkg.description)}</div>` : ''}
          </div>
          <div class="staging-package-actions">
            <button type="button" class="btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline'} btn-staging-select" data-id="${escapeHtml(pkg.id)}">
              ${isSelected ? '✓ Active Target' : 'Select'}
            </button>
            <button type="button" class="btn btn-sm btn-outline btn-staging-edit" data-id="${escapeHtml(pkg.id)}" title="Edit Name, Scenario Tag, Description & Metadata">
              ✏️ Edit
            </button>
            <button type="button" class="btn btn-sm btn-outline btn-staging-export" data-id="${escapeHtml(pkg.id)}" title="Download .akeneopack">
              📦 Download
            </button>
          </div>
        </div>
      `;
    })
    .join('');

  container.querySelectorAll('.btn-staging-select').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) selectStagingPackage(id);
    });
  });

  container.querySelectorAll('.btn-staging-edit').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) openEditStagingPackageModal(id);
    });
  });

  container.querySelectorAll('.btn-staging-export').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) {
        selectStagingPackage(id).then(() => {
          exportStagingPackage();
        });
      }
    });
  });
}

/**
 * Set active entity type filter in staging browser.
 */
export function setStagingFilterType(entityType: string): void {
  state.staging.filterType = entityType || 'all';
  state.staging.page = 1;
  renderStagingItems();
}

/**
 * Set search query string in staging browser.
 */
export function setStagingSearch(search: string): void {
  state.staging.search = (search || '').trim();
  state.staging.page = 1;
  renderStagingItems();
}

/**
 * Set current page in staging browser pagination.
 */
export function setStagingPage(page: number): void {
  state.staging.page = Math.max(1, page);
  renderStagingItems();
}

/**
 * Set page size / limit in staging browser.
 */
export function setStagingLimit(limit: number): void {
  state.staging.limit = Math.max(1, limit);
  state.staging.page = 1;
  renderStagingItems();
}

/**
 * Render category filter tabs with live count badges.
 */
export function renderStagingCategoryTabs(): void {
  const container = document.getElementById('staging-category-tabs');
  if (!container) return;

  const items = state.staging.items || [];
  const currentFilter = state.staging.filterType || 'all';

  // Count items per entity type
  const counts: Record<string, number> = {};
  for (const it of items) {
    counts[it.entity_type] = (counts[it.entity_type] || 0) + 1;
  }

  const allBadge = items.length;

  let html = `
    <button type="button" class="filter-tab-btn staging-category-tab ${currentFilter === 'all' ? 'active' : ''}" data-entity-type="all" role="tab" aria-selected="${currentFilter === 'all' ? 'true' : 'false'}">
      <span>📦</span> All <span class="badge ${allBadge > 0 ? 'badge-primary' : 'badge-neutral'} staging-tab-badge" id="staging-tab-badge-all">${allBadge}</span>
    </button>
  `;

  for (const type of STAGING_ENTITY_TYPES) {
    const count = counts[type.key] || 0;
    const isActive = currentFilter === type.key;
    html += `
      <button type="button" class="filter-tab-btn staging-category-tab ${isActive ? 'active' : ''}" data-entity-type="${escapeHtml(type.key)}" role="tab" aria-selected="${isActive ? 'true' : 'false'}">
        <span>${type.icon}</span> ${escapeHtml(type.label)} <span class="badge ${count > 0 ? 'badge-primary' : 'badge-neutral'} staging-tab-badge">${count}</span>
      </button>
    `;
  }

  container.innerHTML = html;

  container.querySelectorAll('.staging-category-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const type = btn.getAttribute('data-entity-type');
      if (type) setStagingFilterType(type);
    });
  });
}

/**
 * Render pagination controls for the staging entity browser.
 */
export function renderStagingPagination(totalFiltered: number, totalPages: number): void {
  const infoEl = document.getElementById('staging-pagination-info');
  const prevBtn = document.getElementById('staging-btn-prev') as HTMLButtonElement | null;
  const nextBtn = document.getElementById('staging-btn-next') as HTMLButtonElement | null;
  const pagesContainer = document.getElementById('staging-page-numbers');
  const pageSizeSelect = document.getElementById('staging-page-size') as HTMLSelectElement | null;

  const page = state.staging.page || 1;
  const limit = state.staging.limit || 10;

  const start = totalFiltered === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalFiltered);

  if (infoEl) {
    infoEl.textContent = `Showing ${start} - ${end} of ${totalFiltered} items`;
  }

  if (prevBtn) {
    prevBtn.disabled = page <= 1;
  }

  if (nextBtn) {
    nextBtn.disabled = page >= totalPages;
  }

  if (pageSizeSelect && pageSizeSelect.value !== String(limit)) {
    pageSizeSelect.value = String(limit);
  }

  if (pagesContainer) {
    let pagesHtml = '';
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let p = startPage; p <= endPage; p++) {
      pagesHtml += `<button type="button" class="pagination-page-btn ${p === page ? 'active' : ''}" data-page="${p}">${p}</button>`;
    }
    pagesContainer.innerHTML = pagesHtml;

    pagesContainer.querySelectorAll('.pagination-page-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = Number(btn.getAttribute('data-page'));
        if (p && p !== state.staging.page) {
          setStagingPage(p);
        }
      });
    });
  }
}

/**
 * Toggle item selection for staging batch/bulk operations.
 */
export function toggleStagingItemSelection(itemId: string, isSelected: boolean): void {
  if (isSelected) {
    state.staging.selectedItemIds.add(itemId);
  } else {
    state.staging.selectedItemIds.delete(itemId);
  }
  updateStagingBulkActionsUI();
}

/**
 * Select or deselect all items in the current staging view.
 */
export function selectAllStagingItems(isSelected: boolean): void {
  if (isSelected) {
    const items = state.staging.items || [];
    for (const item of items) {
      state.staging.selectedItemIds.add(item.id);
    }
  } else {
    state.staging.selectedItemIds.clear();
  }
  updateStagingBulkActionsUI();
  renderStagingItems();
}

/**
 * Clear all selected items in staging.
 */
export function clearStagingSelection(): void {
  state.staging.selectedItemIds.clear();
  updateStagingBulkActionsUI();
  renderStagingItems();
}

/**
 * Update the bulk actions bar and header checkbox UI.
 */
export function updateStagingBulkActionsUI(): void {
  const bulkBar = document.getElementById('staging-bulk-actions');
  const countText = document.getElementById('staging-selected-count-text');
  const countBadge = document.getElementById('staging-bulk-delete-count');
  const selectAllCb = document.getElementById('staging-select-all-checkbox') as HTMLInputElement | null;

  const selectedCount = state.staging.selectedItemIds.size;
  const totalItems = (state.staging.items || []).length;

  if (bulkBar) {
    bulkBar.style.display = selectedCount > 0 ? 'flex' : 'none';
  }

  if (countText) {
    countText.textContent = `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`;
  }

  if (countBadge) {
    countBadge.textContent = String(selectedCount);
  }

  if (selectAllCb) {
    if (totalItems > 0 && selectedCount === totalItems) {
      selectAllCb.checked = true;
      selectAllCb.indeterminate = false;
    } else if (selectedCount > 0 && selectedCount < totalItems) {
      selectAllCb.checked = false;
      selectAllCb.indeterminate = true;
    } else {
      selectAllCb.checked = false;
      selectAllCb.indeterminate = false;
    }
  }
}

/**
 * Open the bulk delete confirmation modal.
 */
export function openStagingBulkDeleteModal(): void {
  const selectedIds = Array.from(state.staging.selectedItemIds);
  if (selectedIds.length === 0) {
    showToast('Select one or more items to delete', 'warning');
    return;
  }

  const modal = document.getElementById('staging-bulk-delete-modal');
  const countEl = document.getElementById('staging-bulk-delete-modal-count');
  const pkgNameEl = document.getElementById('staging-bulk-delete-modal-package-name');
  const listEl = document.getElementById('staging-bulk-delete-modal-list');

  if (countEl) countEl.textContent = String(selectedIds.length);
  if (pkgNameEl) pkgNameEl.textContent = state.staging.selectedPackage?.name || state.staging.packages.find((p) => p.id === state.staging.selectedPackageId)?.name || (state.staging.selectedPackageId === 'pkg-1' ? 'Virtual Target Alpha' : 'Selected Package');

  if (listEl) {
    const selectedItems = state.staging.items.filter((i) => state.staging.selectedItemIds.has(i.id));
    listEl.innerHTML = selectedItems
      .map((item) => {
        const icon = STAGING_ENTITY_TYPES.find((t) => t.key === item.entity_type)?.icon || '📦';
        const label = getEntityTypeLabel(item.entity_type);
        return `
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.25rem 0; border-bottom: 1px solid var(--border-color, #334155);">
            <div>
              <span class="badge badge-neutral" style="font-size: 0.75rem;">${icon} ${escapeHtml(label)}</span>
              <strong class="mono" style="margin-left: 0.35rem;">${escapeHtml(item.entity_id)}</strong>
            </div>
            <span class="text-muted mono" style="font-size: 0.75rem;">${escapeHtml(item.id)}</span>
          </div>
        `;
      })
      .join('');
  }

  if (modal) modal.style.display = 'flex';
}

/**
 * Close the bulk delete confirmation modal.
 */
export function closeStagingBulkDeleteModal(): void {
  const modal = document.getElementById('staging-bulk-delete-modal');
  if (modal) modal.style.display = 'none';
}

/**
 * Confirm and execute bulk deletion of selected staging items.
 */
export function confirmStagingBulkDelete(): Promise<void> {
  const selectedIds = Array.from(state.staging.selectedItemIds);
  return bulkDeleteStagingItems(selectedIds);
}

/**
 * Bulk delete items from the current staging package.
 */
export async function bulkDeleteStagingItems(itemIds?: string[]): Promise<void> {
  const packageId = state.staging.selectedPackageId;
  const idsToDelete = itemIds && itemIds.length > 0 ? itemIds : Array.from(state.staging.selectedItemIds);

  if (!packageId) {
    showToast('No package selected', 'warning');
    return;
  }

  if (idsToDelete.length === 0) {
    showToast('No items selected for deletion', 'warning');
    return;
  }

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/items/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_ids: idsToDelete }),
    });

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const deletedCount = data.deleted_count ?? idsToDelete.length;

    // Filter out deleted items from local state
    const deletedSet = new Set(idsToDelete);
    state.staging.items = state.staging.items.filter((i) => !deletedSet.has(i.id));

    // Clear deleted items from selection
    for (const id of idsToDelete) {
      state.staging.selectedItemIds.delete(id);
    }

    // Close inspector if inspecting a deleted item
    if (state.staging.inspectItem && deletedSet.has(state.staging.inspectItem.id)) {
      closeStagingItemInspectModal();
    }

    closeStagingBulkDeleteModal();
    showToast(`Removed ${deletedCount} entity item${deletedCount === 1 ? '' : 's'} from package`, 'success');

    renderStagingItems();
    renderStagingCategoryTabs();
    updateStagingBulkActionsUI();
  } catch (err: any) {
    console.warn('Bulk delete staging items failed:', err);
    showToast(err.message || 'Failed to bulk delete items', 'error');
  }
}

/**
 * Format localized multilingual label dictionary into HTML badges.
 */
export function renderLocalizedLabels(labels?: Record<string, string> | null): string {
  if (!labels || typeof labels !== 'object') return '<span class="text-secondary">—</span>';
  const entries = Object.entries(labels).filter(([_, v]) => typeof v === 'string' && v.trim());
  if (entries.length === 0) return '<span class="text-secondary">—</span>';
  return entries
    .map(([locale, label]) => `<span class="badge badge-neutral mono" style="margin-right: 0.25rem;">${escapeHtml(locale)}: ${escapeHtml(label)}</span>`)
    .join(' ');
}

/**
 * Render entity attributes and values table for products / models / reference records.
 */
export function renderEntityValuesTable(values: Record<string, unknown> | null | undefined): string {
  if (!values || typeof values !== 'object' || Object.keys(values).length === 0) {
    return '<p class="text-secondary" style="font-size: 0.85rem; padding: 0.5rem 0;">No attribute values defined.</p>';
  }

  const rows: string[] = [];
  for (const [attrCode, valList] of Object.entries(values)) {
    const list = Array.isArray(valList) ? valList : [valList];
    for (const val of list) {
      if (val && typeof val === 'object') {
        const vObj = val as Record<string, unknown>;
        const locale = vObj.locale ? `<span class="mono badge badge-neutral">${escapeHtml(vObj.locale)}</span>` : '<span class="text-muted">—</span>';
        const scope = (vObj.scope || vObj.channel) ? `<span class="mono badge badge-neutral">${escapeHtml(vObj.scope || vObj.channel)}</span>` : '<span class="text-muted">—</span>';
        const dataVal = vObj.data !== undefined ? vObj.data : (vObj.media_file || vObj.reference_entity_media || vObj.amount || vObj);
        let displayData = '';
        if (typeof dataVal === 'boolean') {
          displayData = dataVal ? '<span class="badge badge-success">Yes</span>' : '<span class="badge badge-neutral">No</span>';
        } else if (typeof dataVal === 'string' || typeof dataVal === 'number') {
          displayData = escapeHtml(String(dataVal));
        } else if (Array.isArray(dataVal)) {
          displayData = dataVal.map((d) => `<span class="dep-pill mono">${escapeHtml(typeof d === 'object' ? JSON.stringify(d) : String(d))}</span>`).join(' ');
        } else {
          displayData = `<code class="mono" style="font-size: 0.75rem;">${escapeHtml(JSON.stringify(dataVal))}</code>`;
        }

        rows.push(`
          <tr>
            <td class="mono font-semibold">${escapeHtml(attrCode)}</td>
            <td>${locale}</td>
            <td>${scope}</td>
            <td>${displayData}</td>
          </tr>
        `);
      } else {
        rows.push(`
          <tr>
            <td class="mono font-semibold">${escapeHtml(attrCode)}</td>
            <td><span class="text-muted">—</span></td>
            <td><span class="text-muted">—</span></td>
            <td>${escapeHtml(String(val))}</td>
          </tr>
        `);
      }
    }
  }

  if (rows.length === 0) {
    return '<p class="text-secondary" style="font-size: 0.85rem; padding: 0.5rem 0;">No attribute values defined.</p>';
  }

  return `
    <div class="table-responsive" style="margin-top: 0.5rem;">
      <table class="data-table">
        <thead>
          <tr>
            <th style="min-width: 140px;">Attribute</th>
            <th style="min-width: 90px;">Locale</th>
            <th style="min-width: 90px;">Channel / Scope</th>
            <th>Value Data</th>
          </tr>
        </thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>
  `;
}

/**
 * Render formatted visual inspector for a staging entity.
 */
export function renderStagingFormattedValues(item: StagingPackageItem): string {
  const payload = safeJsonParse<Record<string, any>>(item.payload);
  if (!payload) {
    return `
      <div class="empty-state-inline text-muted" style="padding: 1.5rem 0; text-align: center;">
        No JSON payload stored for this staging item (identifier: <code>${escapeHtml(item.entity_id)}</code>).
      </div>
    `;
  }

  const type = item.entity_type;
  const label = getEntityTypeLabel(type);

  if (type === 'products' || type === 'product_models') {
    const isProduct = type === 'products';
    const identifier = isProduct ? (payload.identifier || item.entity_id) : (payload.code || item.entity_id);
    const family = payload.family || '—';
    const parent = payload.parent ? `<span class="badge badge-info mono">${escapeHtml(payload.parent)}</span>` : '<span class="text-muted">—</span>';
    const enabled = payload.enabled !== false;
    const categories = Array.isArray(payload.categories) ? payload.categories : [];
    const catBadges = categories.length > 0
      ? categories.map((c: string) => `<span class="badge badge-neutral mono">${escapeHtml(c)}</span>`).join(' ')
      : '<span class="text-muted">None</span>';

    return `
      <div class="entity-inspector-rich">
        <div class="inspector-section" style="margin-bottom: 1rem; padding: 0.75rem; background: var(--surface-secondary, #0f172a); border-radius: 6px; border: 1px solid var(--border-color);">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">${isProduct ? 'Identifier / SKU' : 'Model Code'}</span>
              <strong class="mono" style="font-size: 0.95rem;">${escapeHtml(identifier)}</strong>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Family</span>
              <span class="badge badge-neutral mono">${escapeHtml(family)}</span>
            </div>
            ${isProduct ? `
              <div>
                <span class="text-muted" style="font-size: 0.75rem; display: block;">Parent Model</span>
                ${parent}
              </div>
              <div>
                <span class="text-muted" style="font-size: 0.75rem; display: block;">Status</span>
                <span class="badge ${enabled ? 'badge-success' : 'badge-neutral'}">${enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            ` : `
              <div>
                <span class="text-muted" style="font-size: 0.75rem; display: block;">Family Variant</span>
                <span class="mono">${escapeHtml(payload.family_variant || '—')}</span>
              </div>
            `}
          </div>
          <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
            <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Categories (${categories.length}):</span>
            <div>${catBadges}</div>
          </div>
        </div>

        <div class="inspector-section">
          <div class="inspector-section-title font-semibold" style="margin-bottom: 0.5rem;">
            📦 Attribute Values (${payload.values ? Object.keys(payload.values).length : 0})
          </div>
          ${renderEntityValuesTable(payload.values)}
        </div>
      </div>
    `;
  }

  if (type === 'categories') {
    return `
      <div class="entity-inspector-rich">
        <div class="inspector-section" style="margin-bottom: 1rem; padding: 0.75rem; background: var(--surface-secondary, #0f172a); border-radius: 6px; border: 1px solid var(--border-color);">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Category Code</span>
              <strong class="mono" style="font-size: 0.95rem;">${escapeHtml(payload.code || item.entity_id)}</strong>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Parent Category</span>
              <span class="mono">${escapeHtml(payload.parent || 'Root Category')}</span>
            </div>
          </div>
          <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
            <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Localized Labels:</span>
            <div>${renderLocalizedLabels(payload.labels)}</div>
          </div>
        </div>
      </div>
    `;
  }

  if (type === 'attributes') {
    const options = Array.isArray(payload.options) ? payload.options : [];
    const flags: string[] = [];
    if (payload.scopable) flags.push('Scopable (Channel-specific)');
    if (payload.localizable) flags.push('Localizable');
    if (payload.useable_as_grid_filter) flags.push('Filterable in Grid');
    if (payload.unique) flags.push('Unique');
    if (payload.is_required_for_completeness) flags.push('Required for Completeness');

    return `
      <div class="entity-inspector-rich">
        <div class="inspector-section" style="margin-bottom: 1rem; padding: 0.75rem; background: var(--surface-secondary, #0f172a); border-radius: 6px; border: 1px solid var(--border-color);">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Attribute Code</span>
              <strong class="mono" style="font-size: 0.95rem;">${escapeHtml(payload.code || item.entity_id)}</strong>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Type</span>
              <span class="badge badge-accent mono">${escapeHtml(payload.type || 'text')}</span>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Group</span>
              <span class="badge badge-neutral mono">${escapeHtml(payload.group || '—')}</span>
            </div>
          </div>
          <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
            <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Localized Labels:</span>
            <div>${renderLocalizedLabels(payload.labels)}</div>
          </div>
          ${flags.length > 0 ? `
            <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
              <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Configuration Flags:</span>
              <div style="display: flex; gap: 0.35rem; flex-wrap: wrap;">
                ${flags.map((f) => `<span class="badge badge-neutral" style="font-size: 0.75rem;">✓ ${escapeHtml(f)}</span>`).join('')}
              </div>
            </div>
          ` : ''}
        </div>

        ${options.length > 0 ? `
          <div class="inspector-section">
            <div class="inspector-section-title font-semibold" style="margin-bottom: 0.5rem;">
              🔘 Attribute Options (${options.length})
            </div>
            <div class="table-responsive">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Option Code</th>
                    <th>Sort Order</th>
                    <th>Labels</th>
                  </tr>
                </thead>
                <tbody>
                  ${options.map((opt: any) => `
                    <tr>
                      <td class="mono font-semibold">${escapeHtml(opt.code)}</td>
                      <td>${opt.sort_order ?? '—'}</td>
                      <td>${renderLocalizedLabels(opt.labels)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  if (type === 'families') {
    const attributes = Array.isArray(payload.attribute_codes) ? payload.attribute_codes : (Array.isArray(payload.attributes) ? payload.attributes : []);
    const reqs = payload.attribute_requirements || {};

    return `
      <div class="entity-inspector-rich">
        <div class="inspector-section" style="margin-bottom: 1rem; padding: 0.75rem; background: var(--surface-secondary, #0f172a); border-radius: 6px; border: 1px solid var(--border-color);">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Family Code</span>
              <strong class="mono" style="font-size: 0.95rem;">${escapeHtml(payload.code || item.entity_id)}</strong>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Attributes Count</span>
              <span class="badge badge-neutral font-semibold">${attributes.length}</span>
            </div>
          </div>
          <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
            <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Localized Labels:</span>
            <div>${renderLocalizedLabels(payload.labels)}</div>
          </div>
        </div>

        <div class="inspector-section" style="margin-bottom: 1rem;">
          <div class="inspector-section-title font-semibold" style="margin-bottom: 0.5rem;">
            🏷️ Included Attributes (${attributes.length})
          </div>
          <div style="display: flex; gap: 0.35rem; flex-wrap: wrap; max-height: 160px; overflow-y: auto; padding: 0.5rem; background: var(--surface-primary, #0f172a); border: 1px solid var(--border-color); border-radius: 6px;">
            ${attributes.map((a: string) => `<span class="badge badge-neutral mono">${escapeHtml(a)}</span>`).join('')}
          </div>
        </div>
      </div>
    `;
  }

  if (type === 'users') {
    const roles = Array.isArray(payload.roles) ? payload.roles : [];
    const groups = Array.isArray(payload.user_groups) ? payload.user_groups : (Array.isArray(payload.groups) ? payload.groups : []);

    return `
      <div class="entity-inspector-rich">
        <div class="inspector-section" style="padding: 0.75rem; background: var(--surface-secondary, #0f172a); border-radius: 6px; border: 1px solid var(--border-color);">
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Username</span>
              <strong class="mono" style="font-size: 0.95rem;">${escapeHtml(payload.username || item.entity_id)}</strong>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Email</span>
              <span class="mono">${escapeHtml(payload.email || '—')}</span>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Full Name</span>
              <span>${escapeHtml(`${payload.first_name || ''} ${payload.last_name || ''}`.trim() || '—')}</span>
            </div>
            <div>
              <span class="text-muted" style="font-size: 0.75rem; display: block;">Status</span>
              <span class="badge ${payload.enabled !== false ? 'badge-success' : 'badge-neutral'}">${payload.enabled !== false ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <div style="margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--border-color);">
            <span class="text-muted" style="font-size: 0.75rem; display: block; margin-bottom: 0.25rem;">Roles:</span>
            <div style="display: flex; gap: 0.35rem; flex-wrap: wrap;">
              ${roles.length ? roles.map((r: string) => `<span class="badge badge-accent">${escapeHtml(r)}</span>`).join('') : '<span class="text-muted">—</span>'}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Generic fallback table for other entities
  const entries = Object.entries(payload);
  const rows = entries.map(([k, v]) => {
    let formattedVal = '';
    if (v === null || v === undefined) {
      formattedVal = '<span class="text-muted">—</span>';
    } else if (typeof v === 'object') {
      formattedVal = `<pre class="mono" style="margin: 0; font-size: 0.75rem; max-height: 100px; overflow-y: auto;">${escapeHtml(JSON.stringify(v, null, 2))}</pre>`;
    } else if (typeof v === 'boolean') {
      formattedVal = v ? '<span class="badge badge-success">true</span>' : '<span class="badge badge-neutral">false</span>';
    } else {
      formattedVal = escapeHtml(String(v));
    }
    return `
      <tr>
        <td class="mono font-semibold" style="width: 180px;">${escapeHtml(k)}</td>
        <td>${formattedVal}</td>
      </tr>
    `;
  });

  return `
    <div class="entity-inspector-rich">
      <div class="table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>${rows.join('')}</tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * Render dependency tree view for a staging entity.
 */
export function renderStagingItemDependencies(item: StagingPackageItem): string {
  const deps: Array<{ type: string; id: string; reason: string }> = [];
  const seen = new Set<string>();

  const addDep = (type: string, id: string, reason: string) => {
    if (!id || typeof id !== 'string') return;
    const cleanId = id.trim();
    if (!cleanId) return;
    const key = `${type}:${cleanId}`;
    if (!seen.has(key)) {
      seen.add(key);
      deps.push({ type, id: cleanId, reason });
    }
  };

  // Extract from item.dependencies field if set
  if (item.dependencies) {
    const rawDeps = typeof item.dependencies === 'string' ? safeJsonParse<any>(item.dependencies) : item.dependencies;
    if (Array.isArray(rawDeps)) {
      for (const d of rawDeps) {
        if (typeof d === 'string') {
          const parts = d.split(':');
          if (parts.length >= 2) {
            addDep(parts[0], parts.slice(1).join(':'), 'Explicit Dependency');
          } else {
            addDep('unknown', d, 'Explicit Dependency');
          }
        } else if (d && typeof d === 'object') {
          addDep(d.type || d.entity_type || 'unknown', d.id || d.entity_id || d.code || '', d.reason || 'Explicit Dependency');
        }
      }
    }
  }

  // Extract from payload relationships
  const payload = safeJsonParse<Record<string, any>>(item.payload);
  if (payload) {
    if (payload.family) addDep('families', payload.family, 'Family Schema');
    if (payload.parent) addDep(item.entity_type === 'products' ? 'product_models' : 'categories', payload.parent, 'Parent Entity');
    if (payload.family_variant) addDep('family_variants', payload.family_variant, 'Variant Definition');
    if (payload.group) addDep('attribute_groups', payload.group, 'Attribute Group');
    if (payload.attribute) addDep('attributes', payload.attribute, 'Attribute Definition');

    if (Array.isArray(payload.categories)) {
      for (const c of payload.categories) addDep('categories', c, 'Category Assignment');
    }

    if (Array.isArray(payload.attribute_codes)) {
      for (const a of payload.attribute_codes) addDep('attributes', a, 'Family Attribute');
    }

    if (payload.values && typeof payload.values === 'object') {
      for (const attr of Object.keys(payload.values)) {
        addDep('attributes', attr, 'Used in Values');
      }
    }
  }

  if (deps.length === 0) {
    return `
      <div class="empty-state-inline text-muted" style="padding: 1.5rem 0; text-align: center;">
        No prerequisite dependencies detected for this entity.
      </div>
    `;
  }

  const listItems = deps.map((d) => {
    const icon = STAGING_ENTITY_TYPES.find((t) => t.key === d.type)?.icon || '🔗';
    const typeLabel = getEntityTypeLabel(d.type);
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.4rem 0.6rem; margin-bottom: 0.35rem; background: var(--surface-secondary, #1e293b); border: 1px solid var(--border-color); border-radius: 4px; font-size: 0.85rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          <span class="badge badge-neutral" style="font-size: 0.75rem;">${icon} ${escapeHtml(typeLabel)}</span>
          <span class="mono font-semibold">${escapeHtml(d.id)}</span>
        </div>
        <span class="badge badge-info" style="font-size: 0.75rem;">${escapeHtml(d.reason)}</span>
      </div>
    `;
  }).join('');

  return `
    <div class="staging-deps-tree">
      <div style="margin-bottom: 0.5rem; font-size: 0.85rem; color: var(--text-secondary);">
        Prerequisites required for <strong>${escapeHtml(item.entity_type)}:${escapeHtml(item.entity_id)}</strong> (<span class="font-semibold">${deps.length}</span>):
      </div>
      <div class="staging-deps-list">${listItems}</div>
    </div>
  `;
}

/**
 * Open the staging entity detail inspector drawer/modal.
 */
export async function inspectStagingItem(itemId: string): Promise<void> {
  const item = state.staging.items.find((i) => i.id === itemId);
  if (!item) {
    showToast('Entity not found in staging items', 'warning');
    return;
  }

  state.staging.inspectItem = item;
  state.staging.inspectState.item = item;
  state.staging.inspectState.payloadDraft = getItemPayloadText(item);
  state.staging.inspectState.isValidJson = true;
  state.staging.inspectState.validationError = null;
  state.staging.inspectState.isSaving = false;

  const modal = document.getElementById('staging-item-inspect-modal');
  const iconEl = document.getElementById('staging-inspect-icon');
  const titleEl = document.getElementById('staging-inspect-title');
  const subtitleEl = document.getElementById('staging-inspect-subtitle');
  const tagsEl = document.getElementById('staging-inspect-tags');
  const hashEl = document.getElementById('staging-inspect-hash-display');
  const formattedContainer = document.getElementById('staging-inspect-formatted-content');
  const editorTextarea = document.getElementById('staging-item-payload-editor') as HTMLTextAreaElement | null;
  const depsContainer = document.getElementById('staging-inspect-deps-content');

  const icon = STAGING_ENTITY_TYPES.find((t) => t.key === item.entity_type)?.icon || '🔍';
  const label = getEntityTypeLabel(item.entity_type);
  const pkgName = state.staging.selectedPackage?.name || state.staging.packages.find((p) => p.id === item.package_id)?.name || (item.package_id === 'pkg-1' ? 'Virtual Target Alpha' : item.package_id);

  if (iconEl) iconEl.textContent = icon;
  if (titleEl) titleEl.textContent = `Inspecting ${label}: ${item.entity_id}`;
  if (subtitleEl) subtitleEl.textContent = `Virtual Target Entity Specifications & Payload Editor`;

  if (tagsEl) {
    tagsEl.innerHTML = `
      <span class="badge badge-info">${icon} ${escapeHtml(label)}</span>
      <span class="dep-pill mono font-semibold">${escapeHtml(item.entity_id)}</span>
      <span class="badge badge-neutral">Pkg: ${escapeHtml(pkgName)}</span>
    `;
  }

  if (hashEl) {
    hashEl.textContent = item.hash ? `Hash: ${item.hash.substring(0, 10)}…` : '';
  }

  if (formattedContainer) {
    formattedContainer.innerHTML = renderStagingFormattedValues(item);
  }

  if (editorTextarea) {
    editorTextarea.value = state.staging.inspectState.payloadDraft;
  }

  validateStagingPayloadDraft();

  if (depsContainer) {
    depsContainer.innerHTML = renderStagingItemDependencies(item);
  }

  switchStagingInspectTab(state.staging.inspectState.activeTab || 'values');

  if (modal) modal.style.display = 'flex';
}

/**
 * Close the staging entity detail inspector modal.
 */
export function closeStagingItemInspectModal(): void {
  const modal = document.getElementById('staging-item-inspect-modal');
  if (modal) modal.style.display = 'none';
  state.staging.inspectItem = null;
  state.staging.inspectState.item = null;
}

/**
 * Switch tabs within the staging detail inspector modal.
 */
export function switchStagingInspectTab(tab: 'values' | 'editor' | 'deps'): void {
  const valid = ['values', 'editor', 'deps'];
  if (!valid.includes(tab)) tab = 'values';
  state.staging.inspectState.activeTab = tab;

  const btnValues = document.getElementById('staging-inspect-tab-btn-values');
  const btnEditor = document.getElementById('staging-inspect-tab-btn-editor');
  const btnDeps = document.getElementById('staging-inspect-tab-btn-deps');

  btnValues?.classList.toggle('active', tab === 'values');
  btnEditor?.classList.toggle('active', tab === 'editor');
  btnDeps?.classList.toggle('active', tab === 'deps');

  const viewValues = document.getElementById('staging-inspect-view-values');
  const viewEditor = document.getElementById('staging-inspect-view-editor');
  const viewDeps = document.getElementById('staging-inspect-view-deps');

  if (viewValues) viewValues.style.display = tab === 'values' ? 'block' : 'none';
  if (viewEditor) viewEditor.style.display = tab === 'editor' ? 'block' : 'none';
  if (viewDeps) viewDeps.style.display = tab === 'deps' ? 'block' : 'none';
}

/**
 * Set the inline JSON payload editor draft string.
 */
export function setStagingPayloadDraft(draft: string): void {
  state.staging.inspectState.payloadDraft = draft;
  validateStagingPayloadDraft();
}

/**
 * Validate the JSON payload draft and update validation alert/state.
 */
export function validateStagingPayloadDraft(): boolean {
  const draft = (state.staging.inspectState.payloadDraft || '').trim();
  const errorBox = document.getElementById('staging-item-payload-validation-error');
  const errorMsg = document.getElementById('staging-item-payload-validation-msg');
  const saveBtn = document.getElementById('btn-staging-save-payload') as HTMLButtonElement | null;

  if (!draft) {
    state.staging.inspectState.isValidJson = true;
    state.staging.inspectState.validationError = null;
    if (errorBox) errorBox.style.display = 'none';
    if (saveBtn) saveBtn.disabled = false;
    return true;
  }

  try {
    JSON.parse(draft);
    state.staging.inspectState.isValidJson = true;
    state.staging.inspectState.validationError = null;
    if (errorBox) errorBox.style.display = 'none';
    if (saveBtn) saveBtn.disabled = false;
    return true;
  } catch (err: any) {
    state.staging.inspectState.isValidJson = false;
    state.staging.inspectState.validationError = err.message;
    if (errorBox && errorMsg) {
      errorBox.style.display = 'block';
      errorMsg.textContent = err.message;
    }
    if (saveBtn) saveBtn.disabled = true;
    return false;
  }
}

/**
 * Reformat the JSON payload in the inline editor.
 */
export function formatStagingPayloadDraft(): void {
  const textarea = document.getElementById('staging-item-payload-editor') as HTMLTextAreaElement | null;
  const draft = (state.staging.inspectState.payloadDraft || textarea?.value || '').trim();
  if (!draft) return;

  try {
    const parsed = JSON.parse(draft);
    const formatted = JSON.stringify(parsed, null, 2);
    state.staging.inspectState.payloadDraft = formatted;
    if (textarea) textarea.value = formatted;
    validateStagingPayloadDraft();
    showToast('JSON formatted', 'info', 1000);
  } catch (err: any) {
    showToast(`Cannot format invalid JSON: ${err.message}`, 'error');
  }
}

/**
 * Save updated payload changes from the inline editor.
 */
export async function saveStagingItemPayload(): Promise<boolean> {
  const item = state.staging.inspectItem;
  const packageId = state.staging.selectedPackageId || item?.package_id;

  if (!item || !packageId) {
    showToast('No staging item selected for update', 'warning');
    return false;
  }

  const isValid = validateStagingPayloadDraft();
  if (!isValid) {
    showToast('Fix JSON syntax errors before saving', 'error');
    return false;
  }

  const draft = (state.staging.inspectState.payloadDraft || '').trim();
  let parsedPayload: Record<string, unknown> | null = null;
  if (draft) {
    try {
      parsedPayload = JSON.parse(draft);
    } catch (err: any) {
      showToast(`Invalid JSON: ${err.message}`, 'error');
      return false;
    }
  }

  state.staging.inspectState.isSaving = true;
  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(packageId)}/items/${encodeURIComponent(item.id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payload: parsedPayload }),
    });

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const updatedItem = (data.item || { ...item, payload: parsedPayload }) as StagingPackageItem;

    // Update in local items list
    const idx = state.staging.items.findIndex((i) => i.id === item.id);
    if (idx !== -1) {
      state.staging.items[idx] = updatedItem;
    }
    state.staging.inspectItem = updatedItem;
    state.staging.inspectState.item = updatedItem;

    // Update formatted values view
    const formattedContainer = document.getElementById('staging-inspect-formatted-content');
    if (formattedContainer) {
      formattedContainer.innerHTML = renderStagingFormattedValues(updatedItem);
    }

    // Update dependency tree view
    const depsContainer = document.getElementById('staging-inspect-deps-content');
    if (depsContainer) {
      depsContainer.innerHTML = renderStagingItemDependencies(updatedItem);
    }

    // Update hash display
    const hashEl = document.getElementById('staging-inspect-hash-display');
    if (hashEl && updatedItem.hash) {
      hashEl.textContent = `Hash: ${updatedItem.hash.substring(0, 10)}…`;
    }

    showToast(`Saved changes for ${updatedItem.entity_type}:${updatedItem.entity_id}`, 'success');

    renderStagingItems();
    return true;
  } catch (err: any) {
    console.warn('Save staging item payload failed:', err);
    showToast(err.message || 'Failed to save payload changes', 'error');
    return false;
  } finally {
    state.staging.inspectState.isSaving = false;
  }
}

/**
 * Remove the currently inspected entity from the package.
 */
export async function deleteStagingItemFromInspector(): Promise<void> {
  const item = state.staging.inspectItem;
  if (!item) return;

  const id = item.id;
  closeStagingItemInspectModal();
  await removeStagingItem(id);
}

/**
 * Render curated items for the selected package with search, category filtering & pagination.
 */
export function renderStagingItems(): void {
  const container = document.getElementById('staging-items-list');
  const emptyState = document.getElementById('staging-items-empty');
  const searchEmpty = document.getElementById('staging-search-empty');
  const countBadge = document.getElementById('staging-items-count');
  if (!container) return;

  const items = state.staging.items || [];
  const currentFilter = state.staging.filterType || 'all';
  const searchQuery = (state.staging.search || '').toLowerCase();

  // Filter items by category and text search
  let filtered = items;
  if (currentFilter !== 'all') {
    filtered = filtered.filter((it) => it.entity_type === currentFilter);
  }
  if (searchQuery) {
    filtered = filtered.filter((it) => {
      const idMatch = it.entity_id ? it.entity_id.toLowerCase().includes(searchQuery) : false;
      const typeMatch = it.entity_type ? it.entity_type.toLowerCase().includes(searchQuery) : false;
      const labelMatch = getEntityTypeLabel(it.entity_type).toLowerCase().includes(searchQuery);
      const payloadText = getItemPayloadText(it).toLowerCase();
      const payloadMatch = payloadText.includes(searchQuery);
      return idMatch || typeMatch || labelMatch || payloadMatch;
    });
  }

  const totalFiltered = filtered.length;
  const limit = state.staging.limit || 10;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / limit));

  if (state.staging.page > totalPages) {
    state.staging.page = totalPages;
  }
  if (state.staging.page < 1) {
    state.staging.page = 1;
  }

  const page = state.staging.page || 1;
  const start = totalFiltered === 0 ? 0 : (page - 1) * limit;
  const end = Math.min(start + limit, totalFiltered);
  const pageItems = filtered.slice(start, end);

  if (countBadge) {
    countBadge.textContent = String(items.length);
  }

  if (!items.length) {
    container.innerHTML = '';
    if (emptyState) emptyState.style.display = 'block';
    if (searchEmpty) searchEmpty.style.display = 'none';
    renderStagingCategoryTabs();
    renderStagingPagination(0, 1);
    updateStagingBulkActionsUI();
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  if (!pageItems.length) {
    container.innerHTML = '';
    if (searchEmpty) {
      searchEmpty.style.display = 'block';
      searchEmpty.textContent = `No items match your search or filter criteria.`;
    }
    renderStagingCategoryTabs();
    renderStagingPagination(totalFiltered, totalPages);
    updateStagingBulkActionsUI();
    return;
  }

  if (searchEmpty) searchEmpty.style.display = 'none';

  const rows = pageItems
    .map((item) => {
      const label = getEntityTypeLabel(item.entity_type);
      const icon = STAGING_ENTITY_TYPES.find((t) => t.key === item.entity_type)?.icon || '📦';
      const payload = getItemPayloadText(item);
      const preview = payload ? payload.substring(0, 120).replace(/\n/g, ' ') : '';
      const isSelected = state.staging.selectedItemIds.has(item.id);

      return `
        <tr data-id="${escapeHtml(item.id)}" data-type="${escapeHtml(item.entity_type)}" class="${isSelected ? 'selected' : ''}">
          <td style="text-align: center;">
            <input type="checkbox" class="form-checkbox staging-item-cb" data-id="${escapeHtml(item.id)}" ${isSelected ? 'checked' : ''} />
          </td>
          <td><span class="badge badge-neutral">${icon} ${escapeHtml(label)}</span></td>
          <td class="mono font-semibold">${escapeHtml(item.entity_id)}</td>
          <td class="text-secondary" style="font-size: 0.8rem; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${escapeHtml(preview)}">
            ${preview ? escapeHtml(preview) + '…' : '<span class="text-muted">No payload cached</span>'}
          </td>
          <td style="text-align: right; white-space: nowrap;">
            <button type="button" class="btn btn-xs btn-outline btn-staging-inspect" data-id="${escapeHtml(item.id)}" title="Inspect & Edit Details">Inspect</button>
            <button type="button" class="btn btn-xs btn-outline-danger btn-staging-remove" data-id="${escapeHtml(item.id)}" title="Remove entity from package">Remove</button>
          </td>
        </tr>
      `;
    })
    .join('');

  container.innerHTML = rows;

  container.querySelectorAll('tr[data-id]').forEach((row) => {
    row.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('input') || target.closest('button')) return;
      const id = row.getAttribute('data-id');
      if (id) inspectStagingItem(id);
    });
  });

  container.querySelectorAll('.btn-staging-inspect').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.getAttribute('data-id');
      if (id) inspectStagingItem(id);
    });
  });

  container.querySelectorAll('.btn-staging-remove').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.getAttribute('data-id');
      if (id) removeStagingItem(id);
    });
  });

  container.querySelectorAll('.staging-item-cb').forEach((cb) => {
    cb.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      const id = target.getAttribute('data-id');
      if (id) toggleStagingItemSelection(id, target.checked);
    });
  });

  renderStagingCategoryTabs();
  renderStagingPagination(totalFiltered, totalPages);
  updateStagingBulkActionsUI();
}

/**
 * Render the dependency auto-resolver preview graph.
 */
export function renderStagingGraph(): void {
  const container = document.getElementById('staging-graph-container');
  if (!container) return;

  let stages = state.staging.resolvedStages;

  // Verify whether resolvedStages matches items in the currently selected package.
  // If resolvedStages contains item IDs that do NOT exist in state.staging.items,
  // then resolvedStages is stale (from a previously selected package) and should be cleared.
  if (stages && stages.length > 0 && state.staging.items && state.staging.items.length > 0) {
    const currentItemIds = new Set(state.staging.items.map((it) => it.entity_id));
    const allStageItems = stages.flatMap((s) => s.items);
    const hasMatchingItems = allStageItems.some((id) => currentItemIds.has(id));
    if (!hasMatchingItems) {
      stages = [];
      state.staging.resolvedStages = [];
    }
  }

  // If no explicit auto-resolver graph exists yet, but curated package items exist,
  // derive topological preview stages from the current items grouped by entity_type.
  if ((!stages || !stages.length) && state.staging.items && state.staging.items.length > 0) {
    const grouped = new Map<string, string[]>();
    for (const item of state.staging.items) {
      if (!item.entity_type || !item.entity_id) continue;
      const list = grouped.get(item.entity_type) || [];
      if (!list.includes(item.entity_id)) {
        list.push(item.entity_id);
      }
      grouped.set(item.entity_type, list);
    }
    let stageNum = 1;
    stages = Array.from(grouped.entries()).map(([entityType, items]) => ({
      stage: stageNum++,
      entityType,
      items,
    }));
  }

  if (!stages || !stages.length) {
    container.innerHTML = `
      <div class="empty-state-inline text-muted">
        No resolved dependency graph yet. Add seed entities and run the auto-resolver to preview the topological release order.
      </div>
    `;
    return;
  }

  const html = stages
    .map((stage, idx) => {
      const typeKey = stage.entityType || (stage as any).entity_type || '';
      const typeLabel = getEntityTypeLabel(typeKey);
      const card = `
        <div class="staging-graph-stage">
          <div class="staging-graph-stage-header">
            <span class="staging-graph-stage-num">${stage.stage}</span>
            <span class="staging-graph-stage-type">${escapeHtml(typeLabel)}</span>
            <span class="badge badge-neutral">${stage.items.length}</span>
          </div>
          <div class="staging-graph-stage-items">
            ${stage.items
              .map((id) => `<span class="dep-pill mono">${escapeHtml(id)}</span>`)
              .join('')}
          </div>
        </div>
      `;
      const arrow = idx < stages.length - 1 ? '<div class="staging-graph-arrow" aria-hidden="true">→</div>' : '';
      return card + arrow;
    })
    .join('');

  container.innerHTML = `<div class="staging-graph">${html}</div>`;
}

/**
 * Set the staging import file (from file input or drag-and-drop).
 */
export function setStagingImportFile(file: File | null): void {
  if (file) {
    state.staging.import.file = file;
    state.staging.import.fileName = file.name;
    state.staging.import.fileSize = file.size;
    state.staging.import.error = null;
    state.staging.import.result = null;
    state.staging.import.uploadProgress = 0;
    state.staging.import.statusText = '';
  } else {
    state.staging.import.file = null;
    state.staging.import.fileName = '';
    state.staging.import.fileSize = 0;
    state.staging.import.error = null;
    state.staging.import.result = null;
    state.staging.import.uploadProgress = 0;
    state.staging.import.statusText = '';
  }
  updateStagingImportUI();
}

/**
 * Set an arbitrary field in staging.import state.
 */
export function setStagingImportField(field: keyof StagingImportState, value: any): void {
  (state.staging.import as any)[field] = value;
  updateStagingImportUI();
}

/**
 * Reset staging import state and clear UI elements.
 */
export function resetStagingImport(): void {
  state.staging.import.file = null;
  state.staging.import.fileName = '';
  state.staging.import.fileSize = 0;
  state.staging.import.targetPackageId = '';
  state.staging.import.mode = 'merge';
  state.staging.import.scenario = 'custom';
  state.staging.import.loading = false;
  state.staging.import.uploadProgress = 0;
  state.staging.import.statusText = '';
  state.staging.import.result = null;
  state.staging.import.error = null;

  const fileInput = document.getElementById('staging-import-file-input') as HTMLInputElement | null;
  if (fileInput) fileInput.value = '';

  const modeSelect = document.getElementById('staging-import-mode-select') as HTMLSelectElement | null;
  if (modeSelect) modeSelect.value = 'merge';

  const scenarioSelect = document.getElementById('staging-import-scenario-select') as HTMLSelectElement | null;
  if (scenarioSelect) scenarioSelect.value = 'custom';

  updateStagingImportUI();
}

/**
 * Handle dragover on staging dropzone.
 */
export function handleStagingDragOver(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('staging-import-dropzone');
  zone?.classList.add('dropzone-dragover');
}

/**
 * Handle dragleave on staging dropzone.
 */
export function handleStagingDragLeave(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('staging-import-dropzone');
  zone?.classList.remove('dropzone-dragover');
}

/**
 * Handle drop on staging dropzone.
 */
export function handleStagingDrop(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('staging-import-dropzone');
  zone?.classList.remove('dropzone-dragover');

  const files = e.dataTransfer?.files;
  if (files && files.length > 0) {
    setStagingImportFile(files[0]);
  }
}

/**
 * Handle file input selection on staging import.
 */
export function handleStagingFileSelect(e: Event): void {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    setStagingImportFile(input.files[0]);
  }
}

/**
 * Render options in destination virtual target dropdown.
 */
export function renderStagingImportTargetSelect(): void {
  const select = document.getElementById('staging-import-target-select') as HTMLSelectElement | null;
  if (!select) return;

  const packages = state.staging.packages || [];
  let html = '<option value="">Create new staging package from archive</option>';
  for (const pkg of packages) {
    const isSelected = pkg.id === state.staging.import.targetPackageId;
    html += `<option value="${escapeHtml(pkg.id)}" ${isSelected ? 'selected' : ''}>${escapeHtml(pkg.name)} (${escapeHtml(pkg.status)})</option>`;
  }
  select.innerHTML = html;
  if (state.staging.import.targetPackageId) {
    select.value = state.staging.import.targetPackageId;
  }
}

/**
 * Update the staging file import UI based on current state.
 */
export function updateStagingImportUI(): void {
  const zone = document.getElementById('staging-import-dropzone');
  const statusEl = document.getElementById('staging-import-file-status');
  const infoEl = document.getElementById('staging-import-file-info');
  const submitBtn = document.getElementById('btn-staging-import-submit') as HTMLButtonElement | null;
  const clearBtn = document.getElementById('btn-staging-import-clear') as HTMLButtonElement | null;
  const progressContainer = document.getElementById('staging-import-progress-container');
  const progressBar = document.getElementById('staging-import-progress-bar');
  const progressPct = document.getElementById('staging-import-progress-pct');
  const progressText = document.getElementById('staging-import-progress-text');
  const errorEl = document.getElementById('staging-import-error');
  const resultEl = document.getElementById('staging-import-result');
  const resultBody = document.getElementById('staging-import-result-body');

  const { file, fileName, fileSize, loading, uploadProgress, statusText, error, result } = state.staging.import;

  if (zone) {
    zone.classList.toggle('dropzone-active', loading);
  }

  if (statusEl) {
    statusEl.textContent = fileName
      ? `Selected: ${fileName}`
      : 'Drag a .akeneopack or .zip package here, or click to browse';
  }

  if (infoEl) {
    if (file && fileSize > 0) {
      infoEl.style.display = 'block';
      infoEl.textContent = `Size: ${formatFileSize(fileSize)}`;
    } else {
      infoEl.style.display = 'none';
      infoEl.textContent = '';
    }
  }

  if (submitBtn) {
    submitBtn.disabled = loading || !file;
  }

  if (clearBtn) {
    clearBtn.style.display = file || result || error ? 'inline-block' : 'none';
  }

  if (progressContainer) {
    progressContainer.style.display = loading || (uploadProgress > 0 && uploadProgress < 100) ? 'block' : 'none';
  }

  if (progressBar) {
    progressBar.style.width = `${uploadProgress}%`;
  }

  if (progressPct) {
    progressPct.textContent = `${uploadProgress}%`;
  }

  if (progressText) {
    progressText.textContent = statusText || (loading ? 'Uploading package...' : '');
  }

  if (errorEl) {
    errorEl.style.display = error ? 'block' : 'none';
    errorEl.textContent = error || '';
  }

  if (resultEl && resultBody) {
    if (result && !loading) {
      resultEl.style.display = 'block';
      const pkgName = result.package?.name || (result.manifest as any)?.name || 'Imported Package';
      const count = result.imported_items ?? result.total_items ?? 0;
      const countsMap = result.entity_counts || {};
      const countLines = Object.entries(countsMap)
        .map(([type, c]) => `<li>${escapeHtml(getEntityTypeLabel(type))}: <strong>${c}</strong></li>`)
        .join('');

      resultBody.innerHTML = `
        <div><strong>Target Package:</strong> ${escapeHtml(pkgName)} (<code>${escapeHtml(result.package_id || result.package?.id || '')}</code>)</div>
        <div><strong>Imported Items:</strong> ${count}</div>
        ${countLines ? `<ul style="margin: 0.25rem 0 0 1rem;">${countLines}</ul>` : ''}
      `;
    } else {
      resultEl.style.display = 'none';
      resultBody.innerHTML = '';
    }
  }

  renderStagingImportTargetSelect();
}

/**
 * Upload and import package archive file into a virtual target.
 */
export async function importStagingPackageFile(options?: {
  file?: File;
  targetPackageId?: string;
  mode?: 'merge' | 'replace';
  scenario?: string;
}): Promise<StagingImportResult | null> {
  const fileToUpload = options?.file || state.staging.import.file;
  if (!fileToUpload) {
    showToast('Select or drop a package file first', 'warning');
    return null;
  }

  const targetPackageId =
    options?.targetPackageId !== undefined
      ? options.targetPackageId
      : state.staging.import.targetPackageId;
  const mode = options?.mode || state.staging.import.mode || 'merge';
  const scenario = options?.scenario || state.staging.import.scenario || 'custom';

  state.staging.import.loading = true;
  state.staging.import.error = null;
  state.staging.import.result = null;
  state.staging.import.uploadProgress = 10;
  state.staging.import.statusText = 'Uploading package...';
  updateStagingImportUI();

  try {
    const formData = new FormData();
    formData.append('file', fileToUpload);
    if (targetPackageId) {
      formData.append('package_id', targetPackageId);
    }
    if (mode) {
      formData.append('mode', mode);
    }
    if (scenario) {
      formData.append('scenario', scenario);
    }

    const url = targetPackageId
      ? `/api/v1/staging/packages/${encodeURIComponent(targetPackageId)}/import`
      : '/api/v1/staging/packages/import';

    state.staging.import.uploadProgress = 40;
    state.staging.import.statusText = 'Uploading archive...';
    updateStagingImportUI();

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    state.staging.import.uploadProgress = 80;
    state.staging.import.statusText = 'Extracting and ingesting entities...';
    updateStagingImportUI();

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    const result = data as StagingImportResult;
    state.staging.import.result = result;
    state.staging.import.uploadProgress = 100;
    state.staging.import.statusText = 'Import completed!';
    state.staging.import.loading = false;
    updateStagingImportUI();

    const pkgId = result.package_id || result.package?.id;
    const pkgName = result.package?.name || 'Virtual Target';
    const itemsCount = result.imported_items ?? 0;

    showToast(`Imported ${itemsCount} entities into "${pkgName}"`, 'success');

    // Refresh packages list
    await fetchStagingPackages();

    // Open the imported virtual target directly upon completion
    if (pkgId) {
      await selectStagingPackage(pkgId);
      switchStagingSubTab('curate');
    }

    return result;
  } catch (err: any) {
    console.warn('Import staging package failed:', err);
    state.staging.import.loading = false;
    const errorMsg: string = err?.message || 'Failed to import package archive';
    state.staging.import.error = errorMsg;
    updateStagingImportUI();
    showToast(errorMsg, 'error');
    return null;
  }
}

